import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiDelete, apiGet, apiLogin, apiLogout, apiPatch, apiPost, apiPut, apiRefresh, clearToken, getToken } from '../api';

import './admin.css';

function jsonStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return '';
  }
}

function parseJson(text) {
  if (!text || !String(text).trim()) return { ok: true, value: {} };
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (err) {
    return { ok: false, error: err };
  }
}

function safeTrim(value) {
  return String(value || '').trim();
}

function toDisplayValue(value) {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (_) {
    return String(value);
  }
}

function getDetailFields(tab, item) {
  if (!item) return [];

  const pick = (keys) =>
    keys
      .filter((k) => typeof item[k] !== 'undefined' && item[k] !== null && String(item[k]).length)
      .map((k) => ({ label: k.replace(/_/g, ' '), value: toDisplayValue(item[k]) }));

  if (tab === 'appointments') {
    return pick(['status', 'name', 'phone', 'email', 'preferred_date', 'preferred_time', 'notes', 'createdAt', 'create_at', 'created_at']);
  }
  if (tab === 'inquiries') {
    return pick(['status', 'name', 'phone', 'email', 'message', 'source', 'createdAt', 'create_at', 'created_at']);
  }
  if (tab === 'feedback') {
    return pick(['status', 'name', 'rating', 'message', 'createdAt', 'create_at', 'created_at']);
  }
  if (tab === 'adminUsers') {
    return pick(['name', 'email', 'role', 'is_active', 'last_login_at', 'created_at', 'update_at']);
  }

  return pick(Object.keys(item).slice(0, 10));
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;

  return (
    <div
      className="adm-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div className="adm-modal">
        <div className="adm-modal-head">
          <div className="adm-modal-title">{title}</div>
          <button type="button" className="adm-btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="adm-modal-body">{children}</div>
      </div>
    </div>
  );
}

function toSlug(input) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 120);
}

function parseJwtPayload(jwt) {
  const raw = String(jwt || '');
  const parts = raw.split('.');
  if (parts.length < 2) return {};
  try {
    const base64 = String(parts[1]).replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 ? '='.repeat(4 - (base64.length % 4)) : '';
    const b64 = base64 + pad;
    const atobFn =
      typeof window !== 'undefined' && window.atob
        ? window.atob
        : typeof atob !== 'undefined'
          ? atob
          : null;
    if (!atobFn) return {};
    const jsonText = atobFn(b64);
    const parsed = JSON.parse(jsonText);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function FullAdminPanel() {
  const [token, setTokenState] = useState(() => getToken());
  const didInitAuthRef = useRef(false);
  const [loginEmail, setLoginEmail] = useState(() => {
    return '';
  });
  const [loginPassword, setLoginPassword] = useState(() => {
    return '';
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [activeTab, setActiveTab] = useState('dashboard');

  const headers = useMemo(() => ({}), []);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [appointments, setAppointments] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [doctorsTimeline, setDoctorsTimeline] = useState(null);

  const [apptQuery, setApptQuery] = useState('');
  const [apptStatus, setApptStatus] = useState('all');
  const [inqQuery, setInqQuery] = useState('');
  const [inqStatus, setInqStatus] = useState('all');
  const [adminQuery, setAdminQuery] = useState('');
  const [adminRole, setAdminRole] = useState('all');
  const [adminActive, setAdminActive] = useState('all');
  const [fbQuery, setFbQuery] = useState('');
  const [fbStatus, setFbStatus] = useState('all');
  const [fbRating, setFbRating] = useState('all');
  const [progQuery, setProgQuery] = useState('');
  const [progPublished, setProgPublished] = useState('all');
  const [dtQuery, setDtQuery] = useState('');
  const [dtStatus, setDtStatus] = useState('all');
  const [testQuery, setTestQuery] = useState('');
  const [testPublished, setTestPublished] = useState('all');
  const [clinicProfile, setClinicProfile] = useState(null);
  const [homeContent, setHomeContent] = useState(null);

  const [clinicLocation, setClinicLocation] = useState(null);

  const [clinicLocationForm, setClinicLocationForm] = useState({
    clinicName: '',
    googleMapLink: '',
    embedMapUrl: '',
    latitude: '',
    longitude: '',
    addressText: '',
  });

  const [doctorsTimelineForm, setDoctorsTimelineForm] = useState({
    title: '',
    tagline: '',
    yearLabel: '',
    yearOfEstablishment: '',
    foundersLabel: '',
    foundersText: '',
  });

  const [clinicForm, setClinicForm] = useState({
    name: '',
    tagline: '',
    establishedYear: '',
    establishedYears: '',
    foundersText: '',
    workingDoctorsText: '',
    specializationsText: '',
    primaryPhone: '',
    secondaryPhone: '',
    whatsappNumber: '',
    addressText: '',
    hoursText: '',
    googleMapsUrl: '',
  });

  const [homeForm, setHomeForm] = useState({
    heroTitleLinesText: '',
    heroSubtitle: '',
    ctaPrimary: '',
    ctaSecondary: '',
    heroTagTitle: '',
    heroTagSubtitle: '',
    stat1Value: '',
    stat1Label: '',
    stat2Value: '',
    stat2Label: '',
    stat3Value: '',
    stat3Label: '',
    legacyTitlePrefix: '',
    legacyTitleHighlight: '',
    legacySubtitle: '',
    experienceTitle: '',
    experienceQuote: '',
    experienceAuthor: '',
  });

  const [selectedId, setSelectedId] = useState('');
  const [payloadText, setPayloadText] = useState('{}');

  const [pendingSelection, setPendingSelection] = useState(null);

  const [pendingProfileEdit, setPendingProfileEdit] = useState(false);
  const [pendingCreateFor, setPendingCreateFor] = useState('');

  const [sidebarQuery, setSidebarQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuWrapRef = useRef(null);
  const [profileImageOpen, setProfileImageOpen] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(() => {
    try {
      return window.localStorage.getItem('ARYA_ADMIN_AVATAR') || '';
    } catch (_) {
      return '';
    }
  });
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [notificationsData, setNotificationsData] = useState(null);
  const notifWrapRef = useRef(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsData, setSettingsData] = useState(null);
  const settingsWrapRef = useRef(null);

  const [appSettingsBusy, setAppSettingsBusy] = useState(false);
  const [appSettingsError, setAppSettingsError] = useState('');
  const [appSettings, setAppSettings] = useState([]);
  const [settingsQuery, setSettingsQuery] = useState('');

  const [settingsCrudOpen, setSettingsCrudOpen] = useState(false);
  const [settingsCrudBusy, setSettingsCrudBusy] = useState(false);
  const [settingsCrudError, setSettingsCrudError] = useState('');
  const [settingsCrudMode, setSettingsCrudMode] = useState('create');
  const [settingsCrudId, setSettingsCrudId] = useState('');
  const [settingsCrudForm, setSettingsCrudForm] = useState({ key: '', description: '', valueText: '' });

  const [createOpen, setCreateOpen] = useState(false);
  const [createAdvanced, setCreateAdvanced] = useState(false);
  const [createForm, setCreateForm] = useState({});

  const [myProfileBusy, setMyProfileBusy] = useState(false);
  const [myProfileError, setMyProfileError] = useState('');
  const [myProfile, setMyProfile] = useState(null);

  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [profileEditBusy, setProfileEditBusy] = useState(false);
  const [profileEditError, setProfileEditError] = useState('');
  const [profileEditForm, setProfileEditForm] = useState({ name: '', email: '', password: '' });

  const tokenInfo = useMemo(() => parseJwtPayload(token), [token]);

  const adminId = useMemo(() => {
    const id = tokenInfo && tokenInfo.sub ? String(tokenInfo.sub) : '';
    return id.trim();
  }, [tokenInfo]);

  const profileName = useMemo(() => {
    const name =
      (myProfile && myProfile.name) ||
      (tokenInfo && (tokenInfo.name || tokenInfo.fullName || tokenInfo.username)) ||
      (tokenInfo && tokenInfo.email ? String(tokenInfo.email).split('@')[0] : '') ||
      (loginEmail ? String(loginEmail).split('@')[0] : '') ||
      'Admin';
    return String(name || '').trim() || 'Admin';
  }, [loginEmail, myProfile, tokenInfo]);

  const profileRole = useMemo(() => {
    const role = (myProfile && myProfile.role) || (tokenInfo && (tokenInfo.role || tokenInfo.type));
    return String(role || 'Admin');
  }, [myProfile, tokenInfo]);

  const isAdminRole = useMemo(() => {
    const roleFromToken = tokenInfo && (tokenInfo.role || tokenInfo.type) ? String(tokenInfo.role || tokenInfo.type) : '';
    const roleFromProfile = myProfile && myProfile.role ? String(myProfile.role) : '';
    const raw = String(roleFromToken || roleFromProfile || profileRole || '').trim().toLowerCase();
    return raw === 'admin' || raw === 'super_admin';
  }, [myProfile, profileRole, tokenInfo]);

  const canManageAdminUsers = useMemo(() => {
    const roleFromToken = tokenInfo && (tokenInfo.role || tokenInfo.type) ? String(tokenInfo.role || tokenInfo.type) : '';
    const roleFromProfile = myProfile && myProfile.role ? String(myProfile.role) : '';
    const raw = String(roleFromToken || roleFromProfile || profileRole || '').trim().toLowerCase();
    return raw === 'super_admin';
  }, [isAdminRole, tokenInfo]);

  const defaultPermissionsForRole = useCallback((role) => {
    const r = String(role || '').trim().toLowerCase();
    if (r === 'super_admin' || r === 'admin') {
      return {
        'appointments.manage': true,
        'inquiries.manage': true,
        'feedback.manage': true,
        'notifications.read': true,
        'settings.manage': true,
        'content.manage': true,
        'adminUsers.manage': true,
      };
    }
    if (r === 'editor') {
      return {
        'appointments.manage': false,
        'inquiries.manage': false,
        'feedback.manage': false,
        'notifications.read': true,
        'settings.manage': false,
        'content.manage': true,
        'adminUsers.manage': false,
      };
    }
    return {
      'appointments.manage': false,
      'inquiries.manage': false,
      'feedback.manage': false,
      'notifications.read': false,
      'settings.manage': false,
      'content.manage': false,
      'adminUsers.manage': false,
    };
  }, []);

  useEffect(() => {
    if (didInitAuthRef.current) return;
    didInitAuthRef.current = true;
    apiRefresh()
      .then(() => setTokenState(getToken()))
      .catch(() => undefined);
  }, []);

  const effectivePerms = useMemo(() => {
    if (isAdminRole) return defaultPermissionsForRole('admin');
    const roleFromToken = tokenInfo && (tokenInfo.role || tokenInfo.type) ? String(tokenInfo.role || tokenInfo.type) : '';
    const base = defaultPermissionsForRole(roleFromToken || 'staff');
    const perms = tokenInfo && tokenInfo.perms && typeof tokenInfo.perms === 'object' ? tokenInfo.perms : {};
    return { ...base, ...perms };
  }, [defaultPermissionsForRole, isAdminRole, tokenInfo]);

  const hasPerm = useCallback(
    (key) => {
      if (!key) return true;
      return Boolean(effectivePerms && Object.prototype.hasOwnProperty.call(effectivePerms, key) ? effectivePerms[key] : false);
    },
    [effectivePerms]
  );

  const canAccessTab = useCallback(
    (tab) => {
      const id = String(tab || '');
      if (!id) return false;
      if (id === 'dashboard') return true;
      if (id === 'articles') return true;
      if (id === 'appointments') return hasPerm('appointments.manage');
      if (id === 'inquiries') return hasPerm('inquiries.manage');
      if (id === 'feedback') return hasPerm('feedback.manage');
      if (id === 'adminUsers') return hasPerm('adminUsers.manage');
      if (id === 'settings') return hasPerm('settings.manage');
      if (id === 'homeContent' || id === 'clinicProfile' || id === 'programs' || id === 'testimonials' || id === 'doctorsTimeline' || id === 'clinicLocation') return hasPerm('content.manage');
      return true;
    },
    [hasPerm]
  );

  const profileInitials = useMemo(() => {
    const text = String(profileName || '').trim();
    if (!text) return 'A';
    const parts = text.split(/\s+/).filter(Boolean);
    const first = parts[0] ? String(parts[0][0] || '') : '';
    const last = parts.length > 1 ? String(parts[parts.length - 1][0] || '') : '';
    const initials = (first + last).toUpperCase();
    return initials || String(text[0] || 'A').toUpperCase();
  }, [profileName]);

  const notificationsCount = useMemo(() => {
    const n = notificationsData && notificationsData.counts && typeof notificationsData.counts === 'object' ? notificationsData.counts : {};
    return Number(n.totalNew || 0);
  }, [notificationsData]);

  const canCreateWithForm = useMemo(() => {
    return (
      activeTab === 'appointments' ||
      activeTab === 'adminUsers' ||
      activeTab === 'inquiries' ||
      activeTab === 'programs' ||
      activeTab === 'doctorsTimeline' ||
      activeTab === 'testimonials'
    );
  }, [activeTab]);

  const createTitle = useMemo(() => {
    if (activeTab === 'appointments') return 'Create Appointment Request';
    if (activeTab === 'adminUsers') return 'Create Admin User';
    if (activeTab === 'inquiries') return 'Create Inquiry';
    if (activeTab === 'programs') return 'Create Program';
    if (activeTab === 'doctorsTimeline') return 'Add Doctor';
    if (activeTab === 'testimonials') return 'Create Testimonial';
    return 'Create';
  }, [activeTab]);

  const loadAppointments = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/appointments/requests', { headers });
      setAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load appointment requests');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadClinicLocation = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/clinic-location/admin', { headers });
      const list = Array.isArray(data) ? data : [];
      const active = list.find((d) => d && String(d.status || '') === 'active') || null;
      setClinicLocation(active);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load clinic location');
      setClinicLocation(null);
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadInquiries = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/contact/inquiries', { headers });
      setInquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load inquiries');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadAdminUsers = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/admin-users', { headers });
      setAdminUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load admin users');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadFeedback = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/feedback/admin', { headers });
      setFeedback(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load feedback');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadPrograms = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/care-programs/admin', { headers });
      setPrograms(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load programs');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadDoctorsTimeline = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/doctors-timeline/admin', { headers });
      setDoctorsTimeline(data || null);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load doctors timeline');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadTestimonials = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/content/admin/testimonials', { headers });
      const items = data && typeof data === 'object' && Array.isArray(data.items) ? data.items : [];
      setTestimonials(items);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load testimonials');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadClinicProfile = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/content/admin/clinic-profile', { headers });
      setClinicProfile(data || null);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load clinic profile');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadHomeContent = useCallback(async () => {
    if (!token) return;
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/content/admin/home', { headers });
      setHomeContent(data || null);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load home content');
    } finally {
      setBusy(false);
    }
  }, [headers, token]);

  const loadBrandAssets = useCallback(async () => {
    if (!token) return;
    await Promise.all([loadClinicProfile(), loadHomeContent()]);
  }, [loadClinicProfile, loadHomeContent, token]);

  const uploadImageToCloudinary = useCallback(
    async (file) => {
      if (!file) return '';
      if (!requireTokenOrLogout()) return '';

      setError('');
      setSuccess('');
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const json = await apiPost('/uploads/admin/image', fd, { headers });
        const url = json && json.data && json.data.url ? String(json.data.url) : '';
        if (!url) {
          throw new Error('Upload failed');
        }
        return url;
      } catch (err) {
        setError(err && err.message ? err.message : 'Upload failed');
        return '';
      } finally {
        setBusy(false);
      }
    },
    [headers, token]
  );

  const setClinicLogoUrl = useCallback(
    async (url) => {
      if (!requireTokenOrLogout()) return;
      setError('');
      setSuccess('');
      setBusy(true);
      try {
        await apiPatch('/content/admin/clinic-profile', { logoUrl: String(url || '') }, { headers });
        await loadClinicProfile();
        setSuccess('Logo updated');
      } catch (err) {
        const details = err && err.payload && err.payload.error ? ` ${jsonStringify(err.payload.error)}` : '';
        setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
      } finally {
        setBusy(false);
      }
    },
    [headers, loadClinicProfile, token]
  );

  const setHeroImageUrl = useCallback(
    async (url) => {
      if (!requireTokenOrLogout()) return;
      setError('');
      setSuccess('');
      setBusy(true);
      try {
        await apiPatch('/content/admin/home', { heroImageUrl: String(url || '') }, { headers });
        await loadHomeContent();
        setSuccess('Hero image updated');
      } catch (err) {
        const details = err && err.payload && err.payload.error ? ` ${jsonStringify(err.payload.error)}` : '';
        setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
      } finally {
        setBusy(false);
      }
    },
    [headers, loadHomeContent, token]
  );

  useEffect(() => {
    if (activeTab !== 'clinicProfile') return;
    const c = clinicProfile && typeof clinicProfile === 'object' ? clinicProfile : {};
    setClinicForm({
      name: c.name || '',
      tagline: c.tagline || '',
      establishedYear: c.establishedYear || '',
      establishedYears: c.establishedYears || '',
      foundersText: Array.isArray(c.founders) ? c.founders.filter(Boolean).join('\n') : '',
      workingDoctorsText: Array.isArray(c.workingDoctors) ? c.workingDoctors.filter(Boolean).join('\n') : '',
      specializationsText: Array.isArray(c.specializations) ? c.specializations.filter(Boolean).join('\n') : '',
      primaryPhone: c.primaryPhone || '',
      secondaryPhone: c.secondaryPhone || '',
      whatsappNumber: c.whatsappNumber || '',
      addressText: c.addressText || '',
      hoursText: c.hoursText || '',
      googleMapsUrl: c.googleMapsUrl || '',
    });
    setPayloadText(jsonStringify(c));
  }, [activeTab, clinicProfile]);

  useEffect(() => {
    if (activeTab !== 'clinicLocation') return;

    const c = clinicLocation && typeof clinicLocation === 'object' ? clinicLocation : {};
    setClinicLocationForm({
      clinicName: safeTrim(c.clinicName),
      googleMapLink: safeTrim(c.googleMapLink),
      embedMapUrl: safeTrim(c.embedMapUrl),
      latitude: typeof c.latitude === 'number' ? String(c.latitude) : safeTrim(c.latitude),
      longitude: typeof c.longitude === 'number' ? String(c.longitude) : safeTrim(c.longitude),
      addressText: safeTrim(c.addressText),
    });
  }, [activeTab, clinicLocation]);

  useEffect(() => {
    if (activeTab !== 'homeContent') return;
    const h = homeContent && typeof homeContent === 'object' ? homeContent : {};
    const hero = h.hero && typeof h.hero === 'object' ? h.hero : {};
    const heroTag = h.heroTag && typeof h.heroTag === 'object' ? h.heroTag : {};
    const legacy = h.legacy && typeof h.legacy === 'object' ? h.legacy : {};
    const experience = h.experience && typeof h.experience === 'object' ? h.experience : {};
    const stats = Array.isArray(h.heroStats) ? h.heroStats : [];
    const s1 = stats[0] || {};
    const s2 = stats[1] || {};
    const s3 = stats[2] || {};

    setHomeForm({
      heroTitleLinesText: Array.isArray(hero.titleLines) ? hero.titleLines.filter(Boolean).join('\n') : '',
      heroSubtitle: hero.subtitle || '',
      ctaPrimary: hero.ctaPrimary || '',
      ctaSecondary: hero.ctaSecondary || '',
      heroTagTitle: heroTag.title || '',
      heroTagSubtitle: heroTag.subtitle || '',
      stat1Value: s1.value || '',
      stat1Label: s1.label || '',
      stat2Value: s2.value || '',
      stat2Label: s2.label || '',
      stat3Value: s3.value || '',
      stat3Label: s3.label || '',
      legacyTitlePrefix: legacy.titlePrefix || '',
      legacyTitleHighlight: legacy.titleHighlight || '',
      legacySubtitle: legacy.subtitle || '',
      experienceTitle: experience.title || '',
      experienceQuote: experience.quote || '',
      experienceAuthor: experience.author || '',
    });
    setPayloadText(jsonStringify(h));
  }, [activeTab, homeContent]);

  useEffect(() => {
    if (activeTab !== 'doctorsTimeline') return;
    const tl = doctorsTimeline && typeof doctorsTimeline === 'object' ? doctorsTimeline : {};
    setDoctorsTimelineForm({
      title: tl.title || '',
      tagline: tl.tagline || '',
      yearLabel: tl.yearLabel || 'Year of Establishment',
      yearOfEstablishment: tl.yearOfEstablishment || '',
      foundersLabel: tl.foundersLabel || 'Founder',
      foundersText: Array.isArray(tl.founders) ? tl.founders.filter(Boolean).join('\n') : '',
    });
    setPayloadText(jsonStringify(tl));
  }, [activeTab, doctorsTimeline]);

  useEffect(() => {
    setSelectedId('');
    setPayloadText('{}');
    setCreateOpen(false);
    setCreateAdvanced(false);
    setCreateForm({});
    setShowAdvanced(false);
    setEditForm({});
  }, [activeTab]);

  useEffect(() => {
    if (!pendingSelection) return;
    if (pendingSelection.tab !== activeTab) return;
    setSelectedId(String(pendingSelection.id || ''));
    setPendingSelection(null);
  }, [activeTab, pendingSelection]);

  useEffect(() => {
    if (!pendingCreateFor) return;
    if (activeTab !== pendingCreateFor) return;
    setPendingCreateFor('');
    openCreate();
  }, [activeTab, pendingCreateFor]);

  function onLogin() {
    const email = String(loginEmail || '').trim();
    const password = String(loginPassword || '').trim();
    if (!email || !password) {
      setError('Enter email and password');
      return;
    }
    setError('');
    setBusy(true);
    apiLogin(email, password)
      .then(() => {
        setTokenState(getToken());
        setLoginPassword('');
      })
      .catch((err) => {
        setError(err && err.message ? err.message : 'Login failed');
      })
      .finally(() => setBusy(false));
  }

  function onLogout() {
    apiLogout().catch(() => undefined);
    clearToken();
    setTokenState('');
    setLoginPassword('');
    setSelectedId('');
    setPayloadText('{}');
    setError('');
    setSuccess('');
    setProfileMenuOpen(false);
    setProfileImageOpen(false);
  }

  useEffect(() => {
    if (!profileMenuOpen) return;

    function onDocMouseDown(e) {
      const wrap = profileMenuWrapRef.current;
      if (!wrap) return;
      if (wrap.contains(e.target)) return;
      setProfileMenuOpen(false);
    }

    function onDocKeyDown(e) {
      if (e && e.key === 'Escape') {
        setProfileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [profileMenuOpen]);

  function openProfileMenu() {
    setProfileMenuOpen(true);
    setNotificationsOpen(false);
    setSettingsOpen(false);
  }

  function onSelectProfileAvatarFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setProfileImagePreview(String(reader.result || ''));
      } catch (_) {
        setProfileImagePreview('');
      }
    };
    reader.onerror = () => setProfileImagePreview('');
    reader.readAsDataURL(file);
  }

  function openChangeProfileImage() {
    setProfileMenuOpen(false);
    setProfileImagePreview(profileAvatarUrl || '');
    setProfileImageOpen(true);
  }

  function saveProfileAvatar() {
    const next = String(profileImagePreview || '').trim();
    if (!next) return;
    try {
      window.localStorage.setItem('ARYA_ADMIN_AVATAR', next);
    } catch (_) {
      // ignore
    }
    setProfileAvatarUrl(next);
    setProfileImageOpen(false);
  }

  function requireTokenOrLogout() {
    const fresh = getToken();
    if (fresh) {
      if (fresh !== token) setTokenState(fresh);
      return true;
    }
    clearToken();
    if (token) setTokenState('');
    setError('');
    return false;
  }

  function openCreate() {
    if (!requireTokenOrLogout()) return;
    if (!canCreateWithForm) {
      setError('Create not supported for this section');
      return;
    }

    setError('');
    setCreateAdvanced(false);

    if (activeTab === 'appointments') {
      setCreateForm({ name: '', phone: '', email: '', preferred_date: '', preferred_time: '', notes: '', status: 'new' });
    } else if (activeTab === 'adminUsers') {
      const role = 'admin';
      setCreateForm({ name: '', email: '', password: '', role, is_active: true, permissions: defaultPermissionsForRole(role) });
    } else if (activeTab === 'inquiries') {
      setCreateForm({ name: '', email: '', phone: '', message: '', status: 'new', assigned_to_user_id: '', source: 'admin' });
    } else if (activeTab === 'programs') {
      setCreateForm({ title: '', shortDescription: '', bulletsText: '', iconType: '', displayOrder: 0, status: 'active' });
    } else if (activeTab === 'doctorsTimeline') {
      setCreateForm({ doctorName: '', qualification: '', position: 0, status: 'active' });
    } else if (activeTab === 'testimonials') {
      setCreateForm({ patientDisplayName: '', age: '', category: '', quote: '', outcomeLabel: '', treatmentDuration: '', isPublished: true });
    }

    setCreateOpen(true);
  }

  async function submitCreateForm() {
    if (!requireTokenOrLogout()) return;

    let payload = {};
    if (createAdvanced) {
      const parsed = parseJson(payloadText);
      if (!parsed.ok) {
        setError('Invalid JSON');
        return;
      }
      payload = parsed.value;
    } else if (activeTab === 'adminUsers') {
      payload = {
        name: String(createForm.name || ''),
        email: String(createForm.email || ''),
        password: String(createForm.password || ''),
        role: String(createForm.role || 'admin'),
        is_active: Boolean(createForm.is_active),
        permissions: createForm && createForm.permissions && typeof createForm.permissions === 'object' ? createForm.permissions : defaultPermissionsForRole(createForm.role || 'admin'),
      };
    } else if (activeTab === 'appointments') {
      payload = {
        name: String(createForm.name || ''),
        phone: String(createForm.phone || ''),
        email: String(createForm.email || ''),
        preferred_date: String(createForm.preferred_date || ''),
        preferred_time: String(createForm.preferred_time || ''),
        notes: String(createForm.notes || ''),
        status: String(createForm.status || 'new'),
      };
    } else if (activeTab === 'inquiries') {
      payload = {
        name: String(createForm.name || ''),
        email: String(createForm.email || ''),
        phone: String(createForm.phone || ''),
        message: String(createForm.message || ''),
        status: String(createForm.status || 'new'),
        assigned_to_user_id: String(createForm.assigned_to_user_id || ''),
        source: String(createForm.source || 'admin'),
      };
    } else if (activeTab === 'programs') {
      const bullets = String(createForm.bulletsText || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      payload = {
        title: String(createForm.title || ''),
        shortDescription: String(createForm.shortDescription || ''),
        bulletPoints: bullets.slice(0, 3),
        iconType: String(createForm.iconType || ''),
        displayOrder: Number(createForm.displayOrder || 0),
        status: String(createForm.status || 'active') === 'inactive' ? 'inactive' : 'active',
      };
    } else if (activeTab === 'doctorsTimeline') {
      payload = {
        doctorName: String(createForm.doctorName || ''),
        qualification: String(createForm.qualification || ''),
        position: Number(createForm.position || 0),
        status: String(createForm.status || 'active') === 'inactive' ? 'inactive' : 'active',
      };
    } else if (activeTab === 'testimonials') {
      payload = {
        patientDisplayName: String(createForm.patientDisplayName || ''),
        ...(String(createForm.age || '').trim() ? { age: Number(createForm.age) } : {}),
        category: String(createForm.category || ''),
        quote: String(createForm.quote || ''),
        outcomeLabel: String(createForm.outcomeLabel || ''),
        treatmentDuration: String(createForm.treatmentDuration || ''),
        isPublished: Boolean(createForm.isPublished),
      };
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'appointments') {
        await apiPost('/appointments/requests', payload, { headers });
        await loadAppointments();
      } else if (activeTab === 'adminUsers') {
        await apiPost('/admin-users', payload, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'inquiries') {
        await apiPost('/contact/inquiries', payload, { headers });
        await loadInquiries();
      } else if (activeTab === 'programs') {
        await apiPost('/care-programs/admin', payload, { headers });
        await loadPrograms();
      } else if (activeTab === 'doctorsTimeline') {
        await apiPost('/doctors-timeline/admin/items', payload, { headers });
        await loadDoctorsTimeline();
      } else if (activeTab === 'testimonials') {
        await apiPost('/content/admin/testimonials', payload, { headers });
        await loadTestimonials();
      }
      setCreateOpen(false);
    } catch (err) {
      setError(err && err.message ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDoctorsTimelineSaveForm() {
    if (!requireTokenOrLogout()) return;

    const founders = String(doctorsTimelineForm.foundersText || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 20);

    const payload = {
      title: String(doctorsTimelineForm.title || '').trim(),
      tagline: String(doctorsTimelineForm.tagline || '').trim(),
      yearLabel: String(doctorsTimelineForm.yearLabel || '').trim(),
      yearOfEstablishment: String(doctorsTimelineForm.yearOfEstablishment || '').trim(),
      foundersLabel: String(doctorsTimelineForm.foundersLabel || '').trim(),
      founders,
    };

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      await apiPatch('/doctors-timeline/admin', payload, { headers });
      await loadDoctorsTimeline();
      setSuccess('Your changes are saved');
    } catch (err) {
      const details = err && err.payload && err.payload.error ? ` ${jsonStringify(err.payload.error)}` : '';
      setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicProfileSaveForm() {
    if (!requireTokenOrLogout()) return;

    const founders = String(clinicForm.foundersText || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const workingDoctors = String(clinicForm.workingDoctorsText || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const specializations = String(clinicForm.specializationsText || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      name: String(clinicForm.name || '').trim(),
      tagline: String(clinicForm.tagline || '').trim(),
      establishedYear: String(clinicForm.establishedYear || '').trim(),
      establishedYears: String(clinicForm.establishedYears || '').trim(),
      founders,
      workingDoctors,
      specializations,
      primaryPhone: String(clinicForm.primaryPhone || '').trim(),
      secondaryPhone: String(clinicForm.secondaryPhone || '').trim(),
      whatsappNumber: String(clinicForm.whatsappNumber || '').trim(),
      addressText: String(clinicForm.addressText || '').trim(),
      hoursText: String(clinicForm.hoursText || '').trim(),
      googleMapsUrl: String(clinicForm.googleMapsUrl || '').trim(),
    };

    if (!payload.name || !payload.tagline || !payload.primaryPhone || !payload.addressText || !payload.hoursText) {
      setError('Please fill required fields');
      return;
    }

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      await apiPut('/content/admin/clinic-profile', payload, { headers });
      await loadClinicProfile();
      setSuccess('Your changes are saved');
    } catch (err) {
      const details =
        err && err.payload && err.payload.error
          ? ` ${jsonStringify(err.payload.error)}`
          : '';
      setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleHomeContentSaveForm() {
    if (!requireTokenOrLogout()) return;

    const titleLines = String(homeForm.heroTitleLinesText || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 6);

    const heroStats = [
      { value: String(homeForm.stat1Value || '').trim(), label: String(homeForm.stat1Label || '').trim() },
      { value: String(homeForm.stat2Value || '').trim(), label: String(homeForm.stat2Label || '').trim() },
      { value: String(homeForm.stat3Value || '').trim(), label: String(homeForm.stat3Label || '').trim() },
    ].filter((s) => s.value || s.label);

    const payload = {
      hero: {
        titleLines,
        subtitle: String(homeForm.heroSubtitle || '').trim(),
        ctaPrimary: String(homeForm.ctaPrimary || '').trim(),
        ctaSecondary: String(homeForm.ctaSecondary || '').trim(),
      },
      heroTag: {
        title: String(homeForm.heroTagTitle || '').trim(),
        subtitle: String(homeForm.heroTagSubtitle || '').trim(),
      },
      heroStats,
      legacy: {
        titlePrefix: String(homeForm.legacyTitlePrefix || '').trim(),
        titleHighlight: String(homeForm.legacyTitleHighlight || '').trim(),
        subtitle: String(homeForm.legacySubtitle || '').trim(),
      },
      experience: {
        title: String(homeForm.experienceTitle || '').trim(),
        quote: String(homeForm.experienceQuote || '').trim(),
        author: String(homeForm.experienceAuthor || '').trim(),
      },
    };

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      await apiPut('/content/admin/home', payload, { headers });
      await loadHomeContent();
      setSuccess('Your changes are saved');
    } catch (err) {
      const details =
        err && err.payload && err.payload.error
          ? ` ${jsonStringify(err.payload.error)}`
          : '';
      setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleHomeContentSaveJson(method) {
    if (!requireTokenOrLogout()) return;

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (method === 'PUT') {
        await apiPut('/content/admin/home', parsed.value, { headers });
      } else {
        await apiPatch('/content/admin/home', parsed.value, { headers });
      }
      await loadHomeContent();
    } catch (err) {
      const details =
        err && err.payload && err.payload.error
          ? ` ${jsonStringify(err.payload.error)}`
          : '';
      setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleHomeContentReset() {
    if (!requireTokenOrLogout()) return;

    setError('');
    setBusy(true);
    try {
      await apiDelete('/content/admin/home', { headers });
      await loadHomeContent();
    } catch (err) {
      setError(err && err.message ? err.message : 'Reset failed');
    } finally {
      setBusy(false);
    }
  }

  const loadDashboard = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    setError('');
    setBusy(true);
    try {
      const [appts, inqs, admins, fb, clinic, progs, tests] = await Promise.all([
        hasPerm('appointments.manage') ? apiGet('/appointments/requests', { headers }) : Promise.resolve([]),
        hasPerm('inquiries.manage') ? apiGet('/contact/inquiries', { headers }) : Promise.resolve([]),
        hasPerm('adminUsers.manage') ? apiGet('/admin-users', { headers }) : Promise.resolve([]),
        hasPerm('feedback.manage') ? apiGet('/feedback/admin', { headers }) : Promise.resolve([]),
        hasPerm('content.manage') ? apiGet('/content/admin/clinic-profile', { headers }) : Promise.resolve(null),
        hasPerm('content.manage') ? apiGet('/care-programs/admin', { headers }) : Promise.resolve([]),
        hasPerm('content.manage') ? apiGet('/content/admin/testimonials', { headers }) : Promise.resolve([]),
      ]);

      setAppointments(Array.isArray(appts) ? appts : []);
      setInquiries(Array.isArray(inqs) ? inqs : []);
      setAdminUsers(Array.isArray(admins) ? admins : []);
      setFeedback(Array.isArray(fb) ? fb : []);
      setClinicProfile(clinic || null);
      const progItems = Array.isArray(progs) ? progs : [];
      const testItems = tests && typeof tests === 'object' && Array.isArray(tests.items) ? tests.items : [];
      setPrograms(progItems);
      setTestimonials(testItems);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load dashboard');
    } finally {
      setBusy(false);
    }
  }, [hasPerm, headers, token]);

  const loadSettings = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    setSettingsError('');
    setSettingsBusy(true);
    try {
      const data = await apiGet('/health', { headers });
      setSettingsData(data || null);
    } catch (err) {
      setSettingsError(err && err.message ? err.message : 'Failed to load settings');
    } finally {
      setSettingsBusy(false);
    }
  }, [headers, token]);

  const loadAppSettings = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    setAppSettingsError('');
    setAppSettingsBusy(true);
    try {
      const data = await apiGet('/settings', { headers });
      setAppSettings(Array.isArray(data) ? data : []);
    } catch (err) {
      setAppSettingsError(err && err.message ? err.message : 'Failed to load app settings');
    } finally {
      setAppSettingsBusy(false);
    }
  }, [headers, token]);

  const loadMyProfile = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    if (!adminId) return;
    setMyProfileError('');
    setMyProfileBusy(true);
    try {
      const data = await apiGet(`/admin-users/${adminId}`, { headers });
      setMyProfile(data || null);
      setProfileEditForm({
        name: String(data && data.name ? data.name : ''),
        email: String(data && data.email ? data.email : ''),
        password: '',
      });
    } catch (err) {
      setMyProfileError(err && err.message ? err.message : 'Failed to load profile');
    } finally {
      setMyProfileBusy(false);
    }
  }, [adminId, headers, token]);

  const refresh = useCallback(async () => {
    if (!requireTokenOrLogout()) return;

    if (activeTab === 'dashboard') return loadDashboard();
    if (activeTab === 'settings') {
      await Promise.all([loadSettings(), loadAppSettings(), loadMyProfile()]);
      return;
    }
    if (activeTab === 'appointments') return loadAppointments();
    if (activeTab === 'inquiries') return loadInquiries();
    if (activeTab === 'adminUsers') return loadAdminUsers();
    if (activeTab === 'feedback') return loadFeedback();
    if (activeTab === 'homeContent') return loadHomeContent();
    if (activeTab === 'programs') return loadPrograms();
    if (activeTab === 'doctorsTimeline') return loadDoctorsTimeline();
    if (activeTab === 'testimonials') return loadTestimonials();
    if (activeTab === 'clinicProfile') return loadClinicProfile();
    if (activeTab === 'clinicLocation') return loadClinicLocation();
  }, [activeTab, token, loadAdminUsers, loadAppSettings, loadAppointments, loadClinicLocation, loadClinicProfile, loadDashboard, loadDoctorsTimeline, loadFeedback, loadHomeContent, loadInquiries, loadMyProfile, loadPrograms, loadSettings, loadTestimonials]);

  useEffect(() => {
    if (activeTab !== 'clinicLocation') return;
    if (!token) return;
    loadClinicLocation();
  }, [activeTab, loadClinicLocation, token]);

  async function handleClinicLocationParse() {
    if (!requireTokenOrLogout()) return;
    const googleMapLink = safeTrim(clinicLocationForm.googleMapLink);
    if (!googleMapLink) {
      setError('Paste Google Maps link first');
      return;
    }

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const data = await apiPost('/clinic-location/admin/parse', { googleMapLink }, { headers });
      const payload = data && data.data ? data.data : null;
      if (!payload) throw new Error('Parse failed');

      setClinicLocationForm((s) => ({
        ...s,
        googleMapLink: safeTrim(payload.googleMapLink),
        embedMapUrl: safeTrim(payload.embedMapUrl),
        latitude: typeof payload.latitude === 'number' ? String(payload.latitude) : safeTrim(payload.latitude),
        longitude: typeof payload.longitude === 'number' ? String(payload.longitude) : safeTrim(payload.longitude),
      }));

      setSuccess('Map link processed');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to parse map link');
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicLocationSave() {
    if (!requireTokenOrLogout()) return;

    const googleMapLink = safeTrim(clinicLocationForm.googleMapLink);
    if (!googleMapLink) {
      setError('Paste Google Maps link first');
      return;
    }

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const payload = {
        clinicName: safeTrim(clinicLocationForm.clinicName),
        googleMapLink,
        addressText: safeTrim(clinicLocationForm.addressText),
      };

      await apiPost('/clinic-location/admin', payload, { headers });
      await loadClinicLocation();
      setSuccess('Location saved');
    } catch (err) {
      setError(err && err.message ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicLocationDisable() {
    if (!requireTokenOrLogout()) return;
    if (!clinicLocation || !clinicLocation.id) return;

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      await apiPatch(`/clinic-location/admin/${clinicLocation.id}/disable`, {}, { headers });
      await loadClinicLocation();
      setSuccess('Location disabled');
    } catch (err) {
      setError(err && err.message ? err.message : 'Disable failed');
    } finally {
      setBusy(false);
    }
  }

  const openProfileEdit = useCallback(() => {
    setProfileEditError('');
    if (myProfile) {
      setProfileEditForm({
        name: String(myProfile && myProfile.name ? myProfile.name : ''),
        email: String(myProfile && myProfile.email ? myProfile.email : ''),
        password: '',
      });
    }
    setProfileEditOpen(true);
  }, [myProfile]);

  useEffect(() => {
    if (!pendingProfileEdit) return;
    if (activeTab !== 'settings') return;
    setPendingProfileEdit(false);
    openProfileEdit();
  }, [activeTab, openProfileEdit, pendingProfileEdit]);

  const saveProfile = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    if (!adminId) {
      setProfileEditError('Profile id not found');
      return;
    }

    const name = String(profileEditForm.name || '').trim();
    const email = String(profileEditForm.email || '').trim();
    const password = String(profileEditForm.password || '');

    if (!name) {
      setProfileEditError('Name is required');
      return;
    }
    if (!email) {
      setProfileEditError('Email is required');
      return;
    }
    if (password && password.length < 8) {
      setProfileEditError('Password must be at least 8 characters');
      return;
    }

    setProfileEditError('');
    setProfileEditBusy(true);
    try {
      const payload = { name, email };
      if (password) payload.password = password;
      await apiPatch(`/admin-users/${adminId}`, payload, { headers });
      setProfileEditOpen(false);
      await loadMyProfile();
    } catch (err) {
      const details = err && err.payload && err.payload.error ? ` ${jsonStringify(err.payload.error)}` : '';
      setProfileEditError(`${err && err.message ? err.message : 'Update failed'}${details}`);
    } finally {
      setProfileEditBusy(false);
    }
  }, [adminId, headers, loadMyProfile, profileEditForm, token]);

  const deleteMyAccount = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    if (!adminId) return;
    const ok = typeof window !== 'undefined' && window.confirm ? window.confirm('Delete your admin account? This cannot be undone.') : true;
    if (!ok) return;

    setProfileEditError('');
    setProfileEditBusy(true);
    try {
      await apiDelete(`/admin-users/${adminId}`, { headers });
      setProfileEditOpen(false);
      onLogout();
    } catch (err) {
      setProfileEditError(err && err.message ? err.message : 'Delete failed');
    } finally {
      setProfileEditBusy(false);
    }
  }, [adminId, headers, onLogout, token]);

  const openSettingsCreate = useCallback(() => {
    setSettingsCrudError('');
    setSettingsCrudMode('create');
    setSettingsCrudId('');
    setSettingsCrudForm({ key: '', description: '', valueText: '' });
    setSettingsCrudOpen(true);
  }, []);

  const openSettingsEdit = useCallback((item) => {
    setSettingsCrudError('');
    setSettingsCrudMode('edit');
    setSettingsCrudId(String(item && item.id ? item.id : ''));
    setSettingsCrudForm({
      key: String(item && item.key ? item.key : ''),
      description: String(item && item.description ? item.description : ''),
      valueText: jsonStringify(item && typeof item.value !== 'undefined' ? item.value : ''),
    });
    setSettingsCrudOpen(true);
  }, []);

  const saveSettingsItem = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    setSettingsCrudError('');
    setSettingsCrudBusy(true);
    try {
      const key = String(settingsCrudForm.key || '').trim();
      const description = String(settingsCrudForm.description || '');
      const rawText = String(settingsCrudForm.valueText || '');
      const parsed = parseJson(rawText);
      const value = parsed.ok ? parsed.value : rawText;

      if (!key) {
        setSettingsCrudError('Key is required');
        return;
      }

      if (settingsCrudMode === 'create') {
        await apiPost('/settings', { key, value, description }, { headers });
      } else {
        await apiPatch(`/settings/${settingsCrudId}`, { key, value, description }, { headers });
      }

      setSettingsCrudOpen(false);
      await loadAppSettings();
    } catch (err) {
      const details = err && err.payload && err.payload.error ? ` ${jsonStringify(err.payload.error)}` : '';
      setSettingsCrudError(`${err && err.message ? err.message : 'Save failed'}${details}`);
    } finally {
      setSettingsCrudBusy(false);
    }
  }, [headers, loadAppSettings, settingsCrudForm, settingsCrudId, settingsCrudMode, token]);

  const deleteSettingsItem = useCallback(
    async (id) => {
      if (!requireTokenOrLogout()) return;
      const ok = typeof window !== 'undefined' && window.confirm ? window.confirm('Delete this setting?') : true;
      if (!ok) return;

      setAppSettingsError('');
      setAppSettingsBusy(true);
      try {
        await apiDelete(`/settings/${id}`, { headers });
        await loadAppSettings();
      } catch (err) {
        setAppSettingsError(err && err.message ? err.message : 'Delete failed');
      } finally {
        setAppSettingsBusy(false);
      }
    },
    [headers, loadAppSettings, token]
  );

  const filteredAppSettings = useMemo(() => {
    const q = String(settingsQuery || '').trim().toLowerCase();
    if (!q) return appSettings;
    return (Array.isArray(appSettings) ? appSettings : []).filter((it) => {
      const key = String(it && it.key ? it.key : '').toLowerCase();
      const desc = String(it && it.description ? it.description : '').toLowerCase();
      return key.includes(q) || desc.includes(q);
    });
  }, [appSettings, settingsQuery]);

  const loadNotifications = useCallback(async () => {
    if (!requireTokenOrLogout()) return;
    setNotificationsError('');
    setNotificationsBusy(true);
    try {
      const data = await apiGet('/notifications/admin', { headers });
      setNotificationsData(data || null);
    } catch (err) {
      setNotificationsError(err && err.message ? err.message : 'Failed to load notifications');
    } finally {
      setNotificationsBusy(false);
    }
  }, [headers, token]);

  const openNotifications = useCallback(async () => {
    setNotificationsOpen((v) => !v);
    setSettingsOpen(false);
  }, [loadNotifications]);

  const openSettings = useCallback(async () => {
    setSettingsOpen((v) => !v);
    setNotificationsOpen(false);
  }, []);

  useEffect(() => {
    if (!notificationsOpen) return;
    loadNotifications();
  }, [notificationsOpen, loadNotifications]);

  useEffect(() => {
    if (!token) return;
    loadNotifications();
  }, [token, loadNotifications]);

  useEffect(() => {
    if (!settingsOpen) return;
    loadSettings();
  }, [settingsOpen, loadSettings]);

  useEffect(() => {
    if (!notificationsOpen) return;
    function onDocMouseDown(e) {
      try {
        const el = notifWrapRef.current;
        if (el && e && e.target && !el.contains(e.target)) {
          setNotificationsOpen(false);
        }
      } catch (_) {
        // ignore
      }
    }

    function onDocKeyDown(e) {
      try {
        if (e && (e.key === 'Escape' || e.key === 'Esc')) {
          setNotificationsOpen(false);
        }
      } catch (_) {
        // ignore
      }
    }

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [notificationsOpen]);

  useEffect(() => {
    if (!settingsOpen) return;
    function onDocMouseDown(e) {
      try {
        const el = settingsWrapRef.current;
        if (el && e && e.target && !el.contains(e.target)) {
          setSettingsOpen(false);
        }
      } catch (_) {
        // ignore
      }
    }

    function onDocKeyDown(e) {
      try {
        if (e && (e.key === 'Escape' || e.key === 'Esc')) {
          setSettingsOpen(false);
        }
      } catch (_) {
        // ignore
      }
    }

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [settingsOpen]);

  useEffect(() => {
    if (!token) return;
    refresh();
  }, [activeTab, token]);

  useEffect(() => {
    if (!token) return;
    if (activeTab !== 'settings') return;
    const q = String(settingsQuery || '').toLowerCase();
    if (!q.includes('image')) return;
    loadBrandAssets();
  }, [activeTab, loadBrandAssets, settingsQuery, token]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2600);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    setNotificationsOpen(false);
    setSettingsOpen(false);
  }, [activeTab]);

  const items = useMemo(() => {
    if (activeTab === 'dashboard') return [];
    if (activeTab === 'appointments') return appointments;
    if (activeTab === 'inquiries') return inquiries;
    if (activeTab === 'adminUsers') return adminUsers;
    if (activeTab === 'feedback') return feedback;
    if (activeTab === 'programs') return programs;
    if (activeTab === 'doctorsTimeline') return doctorsTimeline && Array.isArray(doctorsTimeline.timelineItems) ? doctorsTimeline.timelineItems : [];
    if (activeTab === 'testimonials') return testimonials;
    return [];
  }, [activeTab, adminUsers, appointments, doctorsTimeline, feedback, inquiries, programs, testimonials]);

  const dashboardStats = useMemo(() => {
    const apptTotal = appointments.length;
    const apptNew = appointments.filter((a) => String(a && a.status ? a.status : 'new') === 'new').length;

    const todayIso = new Date().toISOString().slice(0, 10);
    const apptToday = appointments.filter((a) => {
      const pref = a && a.preferred_date ? String(a.preferred_date) : '';
      if (pref && String(pref).slice(0, 10) === todayIso) return true;
      const raw = a && (a.createdAt || a.created_at || a.create_at);
      const date = raw ? String(raw).slice(0, 10) : '';
      return date === todayIso;
    }).length;

    const inquiryTotal = inquiries.length;
    const inquiryNew = inquiries.filter((i) => String(i && i.status ? i.status : 'new') === 'new').length;

    const feedbackTotal = feedback.length;
    const feedbackPending = feedback.filter((f) => {
      const s = String(f && f.status ? f.status : 'pending');
      return s === 'pending' || s === 'new';
    }).length;
    const feedbackAvg = feedbackTotal
      ? Math.round(
          (feedback
            .map((f) => Number(f && f.rating ? f.rating : 0))
            .filter((n) => Number.isFinite(n) && n > 0)
            .reduce((a, b) => a + b, 0) /
            Math.max(1, feedback.map((f) => Number(f && f.rating ? f.rating : 0)).filter((n) => Number.isFinite(n) && n > 0).length)) *
            10
        ) / 10
      : 0;

    const programTotal = programs.length;
    const programPublished = programs.filter((p) => String(p && p.status ? p.status : 'active') === 'active').length;

    const testimonialTotal = testimonials.length;
    const testimonialPublished = testimonials.filter((t) => Boolean(t && t.isPublished)).length;

    const adminTotal = adminUsers.length;
    const adminActive = adminUsers.filter((u) => u && u.is_active !== false).length;

    const followupsPending = apptNew + inquiryNew;

    return {
      apptTotal,
      apptNew,
      apptToday,
      inquiryTotal,
      inquiryNew,
      followupsPending,
      feedbackTotal,
      feedbackPending,
      feedbackAvg,
      programTotal,
      programPublished,
      testimonialTotal,
      testimonialPublished,
      adminTotal,
      adminActive,
      clinicName: clinicProfile && clinicProfile.name ? String(clinicProfile.name) : '',
    };
  }, [adminUsers, appointments, clinicProfile, feedback, inquiries, programs, testimonials]);

  const dashboardRecent = useMemo(() => {
    const toTime = (it) => {
      const raw =
        it &&
        (it.createdAt ||
          it.created_at ||
          it.create_at ||
          it.updatedAt ||
          it.updated_at ||
          it.update_at);
      const t = raw ? Date.parse(raw) : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    const recentAppointments = [...appointments].sort((a, b) => toTime(b) - toTime(a)).slice(0, 5);
    const recentInquiries = [...inquiries].sort((a, b) => toTime(b) - toTime(a)).slice(0, 5);
    const recentFeedback = [...feedback].sort((a, b) => toTime(b) - toTime(a)).slice(0, 5);

    return { recentAppointments, recentInquiries, recentFeedback };
  }, [appointments, feedback, inquiries]);

  const selectedItem = useMemo(() => {
    if (!selectedId) return null;
    return items.find((it) => String(it && it.id) === String(selectedId)) || null;
  }, [items, selectedId]);

  const filteredAppointments = useMemo(() => {
    const q = String(apptQuery || '').trim().toLowerCase();
    const status = String(apptStatus || 'all');
    const list = Array.isArray(appointments) ? appointments : [];
    const filtered = list.filter((it) => {
      const s = String(it && it.status ? it.status : 'new');
      if (status !== 'all' && s !== status) return false;

      if (!q) return true;
      const name = String(it && it.name ? it.name : '').toLowerCase();
      const phone = String(it && it.phone ? it.phone : '').toLowerCase();
      const email = String(it && it.email ? it.email : '').toLowerCase();
      const prefDate = String(it && it.preferred_date ? it.preferred_date : '').toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q) || prefDate.includes(q);
    });

    const sortTime = (it) => {
      const raw =
        it &&
        (it.createdAt ||
          it.created_at ||
          it.create_at ||
          it.updatedAt ||
          it.updated_at ||
          it.update_at);
      const t = raw ? Date.parse(String(raw)) : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    return [...filtered].sort((a, b) => sortTime(b) - sortTime(a));
  }, [appointments, apptQuery, apptStatus]);

  const filteredInquiries = useMemo(() => {
    const q = String(inqQuery || '').trim().toLowerCase();
    const status = String(inqStatus || 'all');
    const list = Array.isArray(inquiries) ? inquiries : [];
    const filtered = list.filter((it) => {
      const s = String(it && it.status ? it.status : 'new');
      if (status !== 'all' && s !== status) return false;

      if (!q) return true;
      const name = String(it && it.name ? it.name : '').toLowerCase();
      const phone = String(it && it.phone ? it.phone : '').toLowerCase();
      const email = String(it && it.email ? it.email : '').toLowerCase();
      const msg = String(it && it.message ? it.message : '').toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q) || msg.includes(q);
    });

    const sortTime = (it) => {
      const raw = it && (it.created_at || it.create_at || it.updated_at || it.update_at);
      const t = raw ? Date.parse(String(raw)) : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    return [...filtered].sort((a, b) => sortTime(b) - sortTime(a));
  }, [inquiries, inqQuery, inqStatus]);

  const filteredAdminUsers = useMemo(() => {
    const q = String(adminQuery || '').trim().toLowerCase();
    const role = String(adminRole || 'all');
    const active = String(adminActive || 'all');
    const list = Array.isArray(adminUsers) ? adminUsers : [];

    const filtered = list.filter((it) => {
      const r = String(it && it.role ? it.role : '');
      if (role !== 'all' && r !== role) return false;

      const isActive = it && it.is_active !== false;
      if (active === 'active' && !isActive) return false;
      if (active === 'inactive' && isActive) return false;

      if (!q) return true;
      const name = String(it && it.name ? it.name : '').toLowerCase();
      const email = String(it && it.email ? it.email : '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });

    const sortTime = (it) => {
      const raw = it && (it.updated_at || it.update_at || it.created_at || it.created_at || it.created_at || it.created_at);
      const t = raw ? Date.parse(String(raw)) : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    return [...filtered].sort((a, b) => sortTime(b) - sortTime(a));
  }, [adminActive, adminQuery, adminRole, adminUsers]);

  const filteredFeedback = useMemo(() => {
    const q = String(fbQuery || '').trim().toLowerCase();
    const status = String(fbStatus || 'all');
    const rating = String(fbRating || 'all');
    const list = Array.isArray(feedback) ? feedback : [];

    const filtered = list.filter((it) => {
      const s = String(it && it.status ? it.status : 'pending');
      if (status !== 'all' && s !== status) return false;

      const r = Number(it && typeof it.rating !== 'undefined' ? it.rating : 0);
      if (rating !== 'all' && String(r) !== rating) return false;

      if (!q) return true;
      const name = String(it && it.name ? it.name : '').toLowerCase();
      const msg = String(it && it.message ? it.message : '').toLowerCase();
      return name.includes(q) || msg.includes(q);
    });

    const sortTime = (it) => {
      const raw = it && (it.createdAt || it.create_at || it.updatedAt || it.update_at);
      const t = raw ? Date.parse(String(raw)) : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    return [...filtered].sort((a, b) => sortTime(b) - sortTime(a));
  }, [fbQuery, fbRating, fbStatus, feedback]);

  const filteredPrograms = useMemo(() => {
    const q = String(progQuery || '').trim().toLowerCase();
    const status = String(progPublished || 'all');
    const list = Array.isArray(programs) ? programs : [];

    const filtered = list.filter((it) => {
      const st = String(it && it.status ? it.status : 'active');
      if (status === 'active' && st !== 'active') return false;
      if (status === 'inactive' && st !== 'inactive') return false;

      if (!q) return true;
      const title = String(it && it.title ? it.title : '').toLowerCase();
      const shortDescription = String(it && it.shortDescription ? it.shortDescription : '').toLowerCase();
      return title.includes(q) || shortDescription.includes(q);
    });

    const sortOrder = (it) => {
      const n = Number(it && typeof it.displayOrder !== 'undefined' ? it.displayOrder : 0);
      return Number.isFinite(n) ? n : 0;
    };

    return [...filtered].sort((a, b) => sortOrder(a) - sortOrder(b));
  }, [progPublished, progQuery, programs]);

  const filteredDoctorsTimeline = useMemo(() => {
    const q = String(dtQuery || '').trim().toLowerCase();
    const status = String(dtStatus || 'all');
    const list = doctorsTimeline && Array.isArray(doctorsTimeline.timelineItems) ? doctorsTimeline.timelineItems : [];

    const filtered = list
      .filter((it) => it && it.isDeleted !== true)
      .filter((it) => {
        const st = String(it && it.status ? it.status : 'active');
        if (status === 'active' && st !== 'active') return false;
        if (status === 'inactive' && st !== 'inactive') return false;

        if (!q) return true;
        const name = String(it && it.doctorName ? it.doctorName : '').toLowerCase();
        const qual = String(it && it.qualification ? it.qualification : '').toLowerCase();
        return name.includes(q) || qual.includes(q);
      });

    const pos = (it) => {
      const n = Number(it && typeof it.position !== 'undefined' ? it.position : 0);
      return Number.isFinite(n) ? n : 0;
    };

    return [...filtered].sort((a, b) => pos(a) - pos(b));
  }, [doctorsTimeline, dtQuery, dtStatus]);

  const filteredTestimonials = useMemo(() => {
    const q = String(testQuery || '').trim().toLowerCase();
    const published = String(testPublished || 'all');
    const list = Array.isArray(testimonials) ? testimonials : [];

    const filtered = list.filter((it) => {
      const isPublished = Boolean(it && it.isPublished);
      if (published === 'published' && !isPublished) return false;
      if (published === 'draft' && isPublished) return false;

      if (!q) return true;
      const name = String(it && it.patientDisplayName ? it.patientDisplayName : '').toLowerCase();
      const category = String(it && it.category ? it.category : '').toLowerCase();
      const quote = String(it && it.quote ? it.quote : '').toLowerCase();
      return name.includes(q) || category.includes(q) || quote.includes(q);
    });

    const sortTime = (it) => {
      const raw = it && (it.createdAt || it.create_at || it.updatedAt || it.update_at);
      const t = raw ? Date.parse(String(raw)) : NaN;
      return Number.isFinite(t) ? t : 0;
    };

    return [...filtered].sort((a, b) => sortTime(b) - sortTime(a));
  }, [testPublished, testQuery, testimonials]);

  useEffect(() => {
    if (selectedItem) {
      setPayloadText(jsonStringify(selectedItem));
    }
  }, [selectedItem]);

  useEffect(() => {
    if (!selectedItem) {
      setEditForm({});
      return;
    }

    if (activeTab === 'appointments') {
      setEditForm({
        status: selectedItem.status || 'new',
        name: selectedItem.name || '',
        phone: selectedItem.phone || '',
        email: selectedItem.email || '',
        preferred_date: selectedItem.preferred_date || '',
        preferred_time: selectedItem.preferred_time || '',
        notes: selectedItem.notes || '',
        date: selectedItem.date || '',
        time: selectedItem.time || '',
        reason: selectedItem.reason || '',
      });
      return;
    }

    if (activeTab === 'inquiries') {
      setEditForm({
        status: selectedItem.status || 'new',
        name: selectedItem.name || '',
        email: selectedItem.email || '',
        phone: selectedItem.phone || '',
        message: selectedItem.message || '',
        source: selectedItem.source || '',
        assigned_to_user_id: selectedItem.assigned_to_user_id || '',
      });
      return;
    }

    if (activeTab === 'adminUsers') {
      const perms = selectedItem && selectedItem.permissions && typeof selectedItem.permissions === 'object' ? selectedItem.permissions : {};
      setEditForm({
        name: selectedItem.name || '',
        email: selectedItem.email || '',
        password: '',
        role: selectedItem.role || 'admin',
        is_active: Boolean(selectedItem.is_active),
        permissions: {
          ...defaultPermissionsForRole(selectedItem.role || 'admin'),
          ...perms,
        },
      });
      return;
    }

    if (activeTab === 'feedback') {
      setEditForm({
        status: selectedItem.status || 'pending',
        name: selectedItem.name || '',
        rating: selectedItem.rating || 5,
        message: selectedItem.message || '',
      });
      return;
    }

    if (activeTab === 'programs') {
      const bulletsText = Array.isArray(selectedItem.bulletPoints) ? selectedItem.bulletPoints.join('\n') : '';
      setEditForm({
        title: selectedItem.title || '',
        shortDescription: selectedItem.shortDescription || '',
        bulletsText,
        iconType: selectedItem.iconType || '',
        displayOrder: Number(selectedItem.displayOrder || 0),
        status: String(selectedItem.status || 'active') === 'inactive' ? 'inactive' : 'active',
      });
      return;
    }

    if (activeTab === 'doctorsTimeline') {
      setEditForm({
        doctorName: selectedItem.doctorName || '',
        qualification: selectedItem.qualification || '',
        position: Number(selectedItem.position || 0),
        status: String(selectedItem.status || 'active') === 'inactive' ? 'inactive' : 'active',
      });
      return;
    }

    if (activeTab === 'testimonials') {
      setEditForm({
        patientDisplayName: selectedItem.patientDisplayName || '',
        age: selectedItem.age || '',
        category: selectedItem.category || '',
        quote: selectedItem.quote || '',
        outcomeLabel: selectedItem.outcomeLabel || '',
        treatmentDuration: selectedItem.treatmentDuration || '',
        isPublished: Boolean(selectedItem.isPublished),
      });
      return;
    }

    setEditForm({});
  }, [activeTab, defaultPermissionsForRole, selectedItem]);

  async function handleSaveForm() {
    if (!requireTokenOrLogout()) return;
    if (!selectedId) {
      setError('Select an item first');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'appointments') {
        const payload = {
          status: String(editForm.status || 'new'),
          name: String(editForm.name || ''),
          phone: String(editForm.phone || ''),
          email: String(editForm.email || ''),
          preferred_date: String(editForm.preferred_date || ''),
          preferred_time: String(editForm.preferred_time || ''),
          notes: String(editForm.notes || ''),
          date: String(editForm.date || ''),
          time: String(editForm.time || ''),
          reason: String(editForm.reason || ''),
        };
        await apiPatch(`/appointments/requests/${selectedId}`, payload, { headers });
        await loadAppointments();
      } else if (activeTab === 'inquiries') {
        const payload = {
          status: String(editForm.status || 'new'),
          name: String(editForm.name || ''),
          email: String(editForm.email || ''),
          phone: String(editForm.phone || ''),
          message: String(editForm.message || ''),
          source: String(editForm.source || ''),
          assigned_to_user_id: String(editForm.assigned_to_user_id || ''),
        };
        await apiPatch(`/contact/inquiries/${selectedId}`, payload, { headers });
        await loadInquiries();
      } else if (activeTab === 'adminUsers') {
        const payload = {
          name: String(editForm.name || ''),
          email: String(editForm.email || ''),
          role: String(editForm.role || 'admin'),
          is_active: Boolean(editForm.is_active),
          permissions: editForm && editForm.permissions && typeof editForm.permissions === 'object' ? editForm.permissions : {},
          ...(String(editForm.password || '').trim() ? { password: String(editForm.password || '') } : {}),
        };
        await apiPatch(`/admin-users/${selectedId}`, payload, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'feedback') {
        const payload = {
          status: String(editForm.status || ''),
          ...(String(editForm.name || '').trim() ? { name: String(editForm.name || '') } : {}),
          ...(typeof editForm.rating !== 'undefined' && String(editForm.rating).length ? { rating: Number(editForm.rating) } : {}),
          ...(String(editForm.message || '').trim() ? { message: String(editForm.message || '') } : {}),
        };
        await apiPatch(`/feedback/${selectedId}`, payload, { headers });
        await loadFeedback();
      } else if (activeTab === 'programs') {
        const bullets = String(editForm.bulletsText || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);

        const payload = {
          title: String(editForm.title || ''),
          shortDescription: String(editForm.shortDescription || ''),
          bulletPoints: bullets.slice(0, 3),
          iconType: String(editForm.iconType || ''),
          displayOrder: Number(editForm.displayOrder || 0),
          status: String(editForm.status || 'active') === 'inactive' ? 'inactive' : 'active',
        };

        await apiPatch(`/care-programs/admin/${selectedId}`, payload, { headers });
        await loadPrograms();
      } else if (activeTab === 'doctorsTimeline') {
        const payload = {
          doctorName: String(editForm.doctorName || ''),
          qualification: String(editForm.qualification || ''),
          position: Number(editForm.position || 0),
          status: String(editForm.status || 'active') === 'inactive' ? 'inactive' : 'active',
        };
        await apiPatch(`/doctors-timeline/admin/items/${selectedId}`, payload, { headers });
        await loadDoctorsTimeline();
      } else if (activeTab === 'testimonials') {
        const payload = {
          patientDisplayName: String(editForm.patientDisplayName || ''),
          ...(String(editForm.age || '').trim() ? { age: Number(editForm.age) } : {}),
          category: String(editForm.category || ''),
          quote: String(editForm.quote || ''),
          outcomeLabel: String(editForm.outcomeLabel || ''),
          treatmentDuration: String(editForm.treatmentDuration || ''),
          isPublished: Boolean(editForm.isPublished),
        };

        await apiPatch(`/content/admin/testimonials/${selectedId}`, payload, { headers });
        await loadTestimonials();
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function handlePatch() {
    if (!requireTokenOrLogout()) return;
    if (!selectedId) {
      setError('Select an item first');
      return;
    }

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'appointments') {
        await apiPatch(`/appointments/requests/${selectedId}`, parsed.value, { headers });
        await loadAppointments();
      } else if (activeTab === 'inquiries') {
        await apiPatch(`/contact/inquiries/${selectedId}`, parsed.value, { headers });
        await loadInquiries();
      } else if (activeTab === 'adminUsers') {
        await apiPatch(`/admin-users/${selectedId}`, parsed.value, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'feedback') {
        await apiPatch(`/feedback/${selectedId}`, parsed.value, { headers });
        await loadFeedback();
      } else if (activeTab === 'programs') {
        await apiPatch(`/care-programs/admin/${selectedId}`, parsed.value, { headers });
        await loadPrograms();
      } else if (activeTab === 'doctorsTimeline') {
        await apiPatch(`/doctors-timeline/admin/items/${selectedId}`, parsed.value, { headers });
        await loadDoctorsTimeline();
      } else if (activeTab === 'testimonials') {
        await apiPatch(`/content/admin/testimonials/${selectedId}`, parsed.value, { headers });
        await loadTestimonials();
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!requireTokenOrLogout()) return;
    if (!selectedId) {
      setError('Select an item first');
      return;
    }

    if (typeof window !== 'undefined') {
      const ok = window.confirm('Are you sure you want to delete this item?');
      if (!ok) return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'appointments') {
        await apiDelete(`/appointments/requests/${selectedId}`, { headers });
        await loadAppointments();
      } else if (activeTab === 'inquiries') {
        await apiDelete(`/contact/inquiries/${selectedId}`, { headers });
        await loadInquiries();
      } else if (activeTab === 'adminUsers') {
        await apiDelete(`/admin-users/${selectedId}`, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'feedback') {
        await apiDelete(`/feedback/${selectedId}`, { headers });
        await loadFeedback();
      } else if (activeTab === 'programs') {
        await apiDelete(`/care-programs/admin/${selectedId}`, { headers });
        await loadPrograms();
      } else if (activeTab === 'doctorsTimeline') {
        await apiDelete(`/doctors-timeline/admin/items/${selectedId}`, { headers });
        await loadDoctorsTimeline();
      } else if (activeTab === 'testimonials') {
        await apiDelete(`/content/admin/testimonials/${selectedId}`, { headers });
        await loadTestimonials();
      }
      setSelectedId('');
    } catch (err) {
      setError(err && err.message ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate() {
    if (!requireTokenOrLogout()) return;

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'adminUsers') {
        await apiPost('/admin-users', parsed.value, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'inquiries') {
        await apiPost('/contact/inquiries', parsed.value, { headers });
        await loadInquiries();
      } else if (activeTab === 'programs') {
        await apiPost('/care-programs/admin', parsed.value, { headers });
        await loadPrograms();
      } else if (activeTab === 'doctorsTimeline') {
        await apiPost('/doctors-timeline/admin/items', parsed.value, { headers });
        await loadDoctorsTimeline();
      } else if (activeTab === 'testimonials') {
        await apiPost('/content/admin/testimonials', parsed.value, { headers });
        await loadTestimonials();
      } else {
        setError('Create not supported for this section');
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicProfileSave(method) {
    if (!requireTokenOrLogout()) return;

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (method === 'PUT') {
        await apiPut('/content/admin/clinic-profile', parsed.value, { headers });
      } else {
        await apiPatch('/content/admin/clinic-profile', parsed.value, { headers });
      }
      await loadClinicProfile();
    } catch (err) {
      const details =
        err && err.payload && err.payload.error
          ? ` ${jsonStringify(err.payload.error)}`
          : '';
      setError(`${err && err.message ? err.message : 'Save failed'}${details}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicProfileDelete() {
    if (!requireTokenOrLogout()) return;

    setError('');
    setBusy(true);
    try {
      await apiDelete('/content/admin/clinic-profile', { headers });
      await loadClinicProfile();
    } catch (err) {
      setError(err && err.message ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  const openSimpleTab = useCallback(
    (tab, options) => {
      const id = String(tab || '').trim();
      const opts = options && typeof options === 'object' ? options : {};
      if (!id) return;

      setNotificationsOpen(false);
      setSettingsOpen(false);

      if (id === 'appointments') {
        if (typeof opts.apptStatus === 'string') setApptStatus(opts.apptStatus);
        if (opts.clearQuery) setApptQuery('');
      }

      if (id === 'settings') {
        if (typeof opts.settingsQuery === 'string') setSettingsQuery(opts.settingsQuery);
      }

      setActiveTab(id);
      setSidebarQuery('');
    },
    []
  );

  const sidebarGroups = useMemo(
    () => [
      {
        header: null,
        items: [
          {
            key: 'dashboard',
            tab: 'dashboard',
            label: 'Dashboard',
            sub: 'Overall clinic summary',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 13h8V3H3v10Zm10 8h8V11h-8v10ZM3 21h8v-6H3v6Zm10-18v6h8V3h-8Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('dashboard'),
          },
        ],
      },
      {
        header: 'Website Control',
        items: [
          {
            key: 'home',
            tab: 'homeContent',
            label: 'Home Page Content',
            sub: 'Edit website text',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l9 8h-3v9h-5v-6H11v6H6v-9H3l9-8Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('homeContent'),
          },
          {
            key: 'doctors',
            tab: 'clinicProfile',
            label: 'Doctors Information',
            sub: 'Add / update doctors',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 21V8l9-5 9 5v13h-6v-6H9v6H3Zm8-8h2v-2h2V9h-2V7h-2v2H9v2h2v2Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('clinicProfile'),
          },
          {
            key: 'doctorsTimeline',
            tab: 'doctorsTimeline',
            label: 'Doctors Journey / Timeline',
            sub: 'Manage doctor timeline',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('doctorsTimeline'),
          },
          {
            key: 'clinicLocation',
            tab: 'clinicLocation',
            label: 'Clinic Location / Map Settings',
            sub: 'Update Google Maps location',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7Zm0 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
                  fill="currentColor"
                />
              </svg>
            ),
            onClick: () => openSimpleTab('clinicLocation'),
          },
          {
            key: 'treatments',
            tab: 'programs',
            label: 'Treatments & Services',
            sub: 'Manage treatments list',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 6h16V4H4v2Zm0 7h16v-2H4v2Zm0 7h16v-2H4v2Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('programs'),
          },
          {
            key: 'news',
            tab: 'articles',
            label: 'Clinic News',
            sub: 'Articles & updates',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 2h9l5 5v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm8 1.5V8h4.5L14 3.5ZM7 12h10v2H7v-2Zm0 4h10v2H7v-2Zm0-8h6v2H7V8Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('articles'),
          },
          {
            key: 'images',
            tab: 'settings',
            label: 'Images & Banners',
            sub: 'Logo, photos, sliders',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2Zm-2 0H5V5h14v14Zm-3-3-2.5-3.2L11 16l-1.5-2-2.5 3h9Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('settings', { settingsQuery: 'image' }),
          },
          {
            key: 'seo',
            tab: 'settings',
            label: 'Website SEO',
            sub: 'Google search settings',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M10 2a8 8 0 1 0 4.9 14.3l4.4 4.4 1.4-1.4-4.4-4.4A8 8 0 0 0 10 2Zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('settings', { settingsQuery: 'seo' }),
          },
        ],
      },
      {
        header: 'Appointments',
        items: [
          {
            key: 'appt-new',
            tab: 'appointments',
            label: 'New Appointments',
            sub: 'Need your action',
            status: 'new',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm14 9H3v8a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-8Zm-1-5H4a1 1 0 0 0-1 1v2h18V7a1 1 0 0 0-1-1Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('appointments', { apptStatus: 'new', clearQuery: true }),
          },
          {
            key: 'appt-confirmed',
            tab: 'appointments',
            label: 'Confirmed Appointments',
            sub: 'Scheduled',
            status: 'confirmed',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 16.2 4.8 12l1.4-1.4L9 13.4l8.8-8.8L19.2 6 9 16.2Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('appointments', { apptStatus: 'confirmed', clearQuery: true }),
          },
          {
            key: 'appt-completed',
            tab: 'appointments',
            label: 'Completed Appointments',
            sub: 'Finished visits',
            status: 'completed',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm-1 15-4-4 1.4-1.4L11 14.2l5.6-5.6L18 10l-7 7Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('appointments', { apptStatus: 'completed', clearQuery: true }),
          },
          {
            key: 'messages',
            tab: 'inquiries',
            label: 'Patient Messages',
            sub: 'Website enquiries',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 4H4a2 2 0 0 0-2 2v12l4-3h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 9H5.3L4 14V6h16v7Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('inquiries'),
          },
        ],
      },
      {
        header: 'Patient Feedback',
        items: [
          {
            key: 'reviews',
            tab: 'feedback',
            label: 'Patient Reviews',
            sub: 'Ratings & feedback',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 3v-3H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm16 2H4v10h1.5H6v1.1L7.5 16H20V6Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('feedback'),
          },
          {
            key: 'testimonials',
            tab: 'testimonials',
            label: 'Testimonials',
            sub: 'Success stories to show',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2l2.47 5.01 5.53.8-4 3.9.94 5.52L12 14.77 7.06 17.23 8 11.71 4 7.81l5.53-.8L12 2Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('testimonials'),
          },
        ],
      },
      {
        header: 'Admin Users',
        items: [
          {
            key: 'admins',
            tab: 'adminUsers',
            label: 'Admin Accounts',
            sub: 'Who can access',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.41 0-8 2-8 4.5V21h16v-2.5C20 16 16.41 14 12 14Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('adminUsers'),
          },
        ],
      },
      {
        header: 'Settings',
        items: [
          {
            key: 'settings',
            tab: 'settings',
            label: 'System Settings',
            sub: 'Website & system options',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.06 7.06 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 1h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.7 7.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.3.6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54c.04.24.25.42.49.42h3.8c.24 0 .45-.18.49-.42l.36-2.54c.58-.23 1.12-.54 1.63-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => openSimpleTab('settings', { settingsQuery: '' }),
          },
          {
            key: 'password',
            tab: 'settings',
            label: 'Change Password',
            sub: 'Update your login password',
            icon: (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm-3 8V6a3 3 0 0 1 6 0v3H9Zm3 4a2 2 0 0 1 1 3.73V18h-2v-1.27A2 2 0 0 1 12 13Z" fill="currentColor" />
              </svg>
            ),
            onClick: () => {
              openSimpleTab('settings');
              setPendingProfileEdit(true);
            },
          },
        ],
      },
    ],
    [openSimpleTab]
  );

  const filteredSidebarGroups = useMemo(() => {
    const q = String(sidebarQuery || '').trim().toLowerCase();

    const canSeeItem = (it) => {
      if (!it) return false;
      return canAccessTab(it.tab);
    };

    const matches = (text) => {
      const v = String(text || '').toLowerCase();
      return q ? v.includes(q) : true;
    };

    const base = (sidebarGroups || [])
      .map((g) => {
        const items = Array.isArray(g.items) ? g.items.filter(canSeeItem) : [];
        return { ...g, items };
      })
      .filter((g) => (Array.isArray(g.items) ? g.items.length : 0) > 0);

    if (!q) return base;

    const filtered = base
      .map((g) => {
        const headerMatch = matches(g.header);
        const items = (g.items || []).filter((it) => headerMatch || matches(it.label) || matches(it.sub));
        return { ...g, items };
      })
      .filter((g) => (Array.isArray(g.items) ? g.items.length : 0) > 0);

    const activePresent = filtered.some((g) => (g.items || []).some((it) => it && it.tab === activeTab));
    if (activePresent) return filtered;

    const activeFromBase = base
      .map((g) => ({ ...g, items: (g.items || []).filter((it) => it && it.tab === activeTab) }))
      .filter((g) => (Array.isArray(g.items) ? g.items.length : 0) > 0);

    return activeFromBase.length ? [...activeFromBase, ...filtered] : filtered;
  }, [activeTab, canAccessTab, sidebarGroups, sidebarQuery]);

  useEffect(() => {
    if (activeTab && !canAccessTab(activeTab)) {
      setActiveTab('dashboard');
    }
  }, [activeTab, canAccessTab]);

  const pageTitle = useMemo(() => {
    if (activeTab === 'appointments') {
      if (apptStatus === 'new') return 'New Appointments';
      if (apptStatus === 'confirmed') return 'Confirmed Appointments';
      if (apptStatus === 'completed') return 'Completed Appointments';
      return 'Appointments';
    }
    if (activeTab === 'inquiries') return 'Patient Messages';
    if (activeTab === 'homeContent') return 'Home Page Content';
    if (activeTab === 'clinicProfile') return 'Doctors Information';
    if (activeTab === 'clinicLocation') return 'Clinic Location / Map Settings';
    if (activeTab === 'doctorsTimeline') return 'Doctors Journey / Timeline';
    if (activeTab === 'programs') return 'Treatments & Services';
    if (activeTab === 'testimonials') return 'Testimonials';
    if (activeTab === 'feedback') return 'Patient Reviews';
    if (activeTab === 'adminUsers') return 'Admin Accounts';
    if (activeTab === 'articles') return 'Clinic News';
    if (activeTab === 'settings') {
      const q = String(settingsQuery || '').toLowerCase();
      if (q.includes('image')) return 'Images & Banners';
      if (q.includes('seo')) return 'Website SEO';
      return 'System Settings';
    }
    if (activeTab === 'dashboard') return 'Dashboard';
    return 'Admin';
  }, [activeTab, apptStatus, settingsQuery]);

  function renderList() {
    if (activeTab === 'dashboard') {
      return (
        <div className="adm-dashboard">
          <div className="adm-card adm-dash-welcome">
            <div className="adm-dash-welcome-title">Welcome{dashboardStats.clinicName ? ` to ${dashboardStats.clinicName}` : ''}</div>
            <div className="adm-dash-welcome-sub">Use this page for a quick overview. Click any card to open that section.</div>
          </div>

          <div className="adm-stat-grid">
            <button type="button" className="adm-stat-card adm-card adm-stat-card--click" onClick={() => openSimpleTab('inquiries')}>
              <div className="adm-stat-head">
                <div className="adm-stat-icon adm-stat-icon--green" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path
                      d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.41 0-8 2-8 4.5V21h16v-2.5C20 16 16.41 14 12 14Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="adm-stat-k">Total Patients</div>
              </div>
              <div className="adm-stat-v">{dashboardStats.inquiryTotal}</div>
              <div className="adm-stat-sub">New messages: {dashboardStats.inquiryNew}</div>
            </button>

            <button type="button" className="adm-stat-card adm-card adm-stat-card--click" onClick={() => openSimpleTab('appointments', { apptStatus: 'all', clearQuery: true })}>
              <div className="adm-stat-head">
                <div className="adm-stat-icon adm-stat-icon--blue" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path
                      d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm14 9H3v8a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-8Zm-1-5H4a1 1 0 0 0-1 1v2h18V7a1 1 0 0 0-1-1Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="adm-stat-k">Appointments Today</div>
              </div>
              <div className="adm-stat-v">{dashboardStats.apptToday}</div>
              <div className="adm-stat-sub">New requests: {dashboardStats.apptNew}</div>
            </button>

            <button type="button" className="adm-stat-card adm-card adm-stat-card--click" onClick={() => openSimpleTab('programs')}>
              <div className="adm-stat-head">
                <div className="adm-stat-icon adm-stat-icon--violet" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path
                      d="M6 2h12a2 2 0 0 1 2 2v18l-8-4-8 4V4a2 2 0 0 1 2-2Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="adm-stat-k">Active Treatments</div>
              </div>
              <div className="adm-stat-v">{dashboardStats.programPublished}</div>
              <div className="adm-stat-sub">Total: {dashboardStats.programTotal}</div>
            </button>

            <button type="button" className="adm-stat-card adm-card adm-stat-card--click" onClick={() => openSimpleTab('feedback')}>
              <div className="adm-stat-head">
                <div className="adm-stat-icon adm-stat-icon--pink" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path
                      d="M12 2l2.47 5.01 5.53.8-4 3.9.94 5.52L12 14.77 7.06 17.23 8 11.71 4 7.81l5.53-.8L12 2Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="adm-stat-k">Pending Reviews</div>
              </div>
              <div className="adm-stat-v">{dashboardStats.feedbackPending}</div>
              <div className="adm-stat-sub">Average rating: {dashboardStats.feedbackAvg || 0}</div>
            </button>
          </div>

          <div className="adm-dashboard-grid">
            <div className="adm-card adm-dash-panel">
              <div className="adm-dash-panel-title">Today’s Appointments</div>
              <div className="adm-dash-panel-sub">Who is expected today</div>
              <div className="adm-dash-list">
                {(() => {
                  const todayIso = new Date().toISOString().slice(0, 10);
                  const toTime = (it) => {
                    const raw = it && (it.createdAt || it.created_at || it.create_at || it.updatedAt || it.updated_at || it.update_at);
                    const t = raw ? Date.parse(String(raw)) : NaN;
                    return Number.isFinite(t) ? t : 0;
                  };
                  const list = [...appointments]
                    .filter((a) => {
                      const pref = a && a.preferred_date ? String(a.preferred_date) : '';
                      if (pref && String(pref).slice(0, 10) === todayIso) return true;
                      const raw = a && (a.createdAt || a.created_at || a.create_at);
                      const date = raw ? String(raw).slice(0, 10) : '';
                      return date === todayIso;
                    })
                    .sort((a, b) => toTime(b) - toTime(a))
                    .slice(0, 6);

                  return list.map((it) => (
                    <button
                      key={String(it && it.id ? it.id : '')}
                      type="button"
                      className="adm-dash-row"
                      onClick={() => {
                        setPendingSelection({ tab: 'appointments', id: String(it && it.id ? it.id : '') });
                        openSimpleTab('appointments', { apptStatus: 'all', clearQuery: true });
                      }}
                    >
                      <div className="adm-dash-row-title">{String(it && it.name ? it.name : '—')}</div>
                      <div className="adm-dash-row-sub">{String(it && it.preferred_time ? it.preferred_time : it && it.status ? it.status : '—')}</div>
                    </button>
                  ));
                })()}
                {!dashboardStats.apptToday ? <div className="adm-muted">No appointments scheduled for today</div> : null}
              </div>
            </div>

            <div className="adm-card adm-dash-panel">
              <div className="adm-dash-panel-title">Recent Patient Messages</div>
              <div className="adm-dash-panel-sub">Latest enquiries from the website</div>
              <div className="adm-dash-list">
                {dashboardRecent.recentInquiries.map((it) => (
                  <button
                    key={String(it && it.id ? it.id : '')}
                    type="button"
                    className="adm-dash-row"
                    onClick={() => {
                      setPendingSelection({ tab: 'inquiries', id: String(it && it.id ? it.id : '') });
                      openSimpleTab('inquiries');
                    }}
                  >
                    <div className="adm-dash-row-title">{String(it && it.name ? it.name : '—')}</div>
                    <div className="adm-dash-row-sub">{String(it && it.message ? String(it.message).slice(0, 42) : it && it.status ? it.status : '—')}{String(it && it.message ? it.message : '').length > 42 ? '…' : ''}</div>
                  </button>
                ))}
                {!dashboardRecent.recentInquiries.length ? <div className="adm-muted">No messages yet</div> : null}
              </div>
            </div>

            <div className="adm-card adm-dash-actions-card">
              <div className="adm-dash-panel-title">Quick Actions</div>
              <div className="adm-dash-panel-sub">Common tasks for your clinic</div>

              <div className="adm-dash-actions">
                <button type="button" className="adm-btn adm-btn--primary" onClick={() => openSimpleTab('homeContent')}>
                  Edit Home Page
                </button>
                <button type="button" className="adm-btn" onClick={() => openSimpleTab('clinicProfile')}>
                  Update Doctors
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn--success"
                  onClick={() => {
                    setPendingCreateFor('programs');
                    openSimpleTab('programs');
                  }}
                >
                  Add Treatment
                </button>
                <button type="button" className="adm-btn" onClick={() => openSimpleTab('settings', { settingsQuery: 'image' })}>
                  Upload Images
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'clinicLocation') {
      const previewUrl = safeTrim(clinicLocationForm.embedMapUrl);
      const hasPreview = Boolean(previewUrl);
      const hasActive = Boolean(clinicLocation && clinicLocation.id && String(clinicLocation.status || '') === 'active');

      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div>
              <div className="adm-list-title">Clinic Location / Map Settings</div>
              <div className="adm-muted" style={{ marginTop: 2 }}>Paste Google Maps link and save. Website updates automatically.</div>
            </div>

            <div className="adm-sec-actions">
              <button type="button" className="adm-btn" onClick={() => loadClinicLocation()} disabled={busy}>
                Refresh
              </button>
              {hasActive ? (
                <button type="button" className="adm-btn adm-btn--danger" onClick={handleClinicLocationDisable} disabled={busy}>
                  Disable
                </button>
              ) : null}
              <button type="button" className="adm-btn adm-btn--primary" onClick={handleClinicLocationSave} disabled={busy}>
                Save / Update
              </button>
            </div>
          </div>

          <div className="adm-form-grid">
            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Map Link</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Paste Google Maps link here</label>
                  <input
                    className="adm-input"
                    value={clinicLocationForm.googleMapLink}
                    onChange={(e) => setClinicLocationForm((s) => ({ ...s, googleMapLink: e.target.value }))}
                    placeholder="https://maps.app.goo.gl/..."
                  />
                  <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button type="button" className="adm-btn" onClick={handleClinicLocationParse} disabled={busy}>
                      Extract & Preview
                    </button>
                  </div>
                </div>

                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                    <label className="adm-label">Clinic Name</label>
                    <input className="adm-input" value={clinicLocationForm.clinicName} onChange={(e) => setClinicLocationForm((s) => ({ ...s, clinicName: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                    <label className="adm-label">Address</label>
                    <input className="adm-input" value={clinicLocationForm.addressText} onChange={(e) => setClinicLocationForm((s) => ({ ...s, addressText: e.target.value }))} />
                  </div>
                </div>

                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                    <label className="adm-label">Latitude</label>
                    <input className="adm-input" value={clinicLocationForm.latitude} readOnly />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                    <label className="adm-label">Longitude</label>
                    <input className="adm-input" value={clinicLocationForm.longitude} readOnly />
                  </div>
                </div>

                <div className="adm-field">
                  <label className="adm-label">Embed URL</label>
                  <input className="adm-input" value={clinicLocationForm.embedMapUrl} readOnly />
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Preview</div>
              <div className="adm-form-body">
                {hasPreview ? (
                  <div style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--adm-border)' }}>
                    <iframe
                      title="Map preview"
                      src={previewUrl}
                      style={{ width: '100%', height: '100%', border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="adm-muted">Paste a link and click Extract & Preview to see the map here.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'doctorsTimeline') {
      const count = filteredDoctorsTimeline.length;
      const tl = doctorsTimeline && typeof doctorsTimeline === 'object' ? doctorsTimeline : {};

      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={dtQuery}
                onChange={(e) => setDtQuery(e.target.value)}
                placeholder="Search doctors (name)…"
              />
              <select className="adm-select adm-sec-filter" value={dtStatus} onChange={(e) => setDtStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button
                type="button"
                className="adm-btn"
                onClick={() => {
                  setDtQuery('');
                  setDtStatus('all');
                }}
                disabled={!String(dtQuery || '').trim() && dtStatus === 'all'}
              >
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add Doctor
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Doctors (Timeline)</div>
              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No doctors yet</div>
                    <div className="adm-empty-sub">Add doctors to build your timeline.</div>
                    <button type="button" className="adm-btn adm-btn--primary" onClick={openCreate}>
                      Add Doctor
                    </button>
                  </div>
                ) : (
                  filteredDoctorsTimeline.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const st = String(it && it.status ? it.status : 'active');
                    const name = String(it && it.doctorName ? it.doctorName : '—');
                    const qual = String(it && it.qualification ? it.qualification : '');
                    const pos = String(typeof it.position !== 'undefined' ? it.position : 0);
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{name}</div>
                          <div className="adm-card-row-chips">
                            <span className={`adm-chip ${st === 'active' ? 'adm-chip--confirmed' : 'adm-chip--inactive'}`}>{st}</span>
                          </div>
                        </div>
                        <div className="adm-card-row-sub">
                          <div className="adm-card-row-meta">Order: {pos}</div>
                        </div>
                        {qual ? <div className="adm-card-row-msg">{qual}</div> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Timeline Settings</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>Controls the heading, year, founders, and doctor list shown on the website.</div>
                </div>
                <div className="adm-sec-actions">
                  <button type="button" className="adm-btn adm-btn--primary" onClick={handleDoctorsTimelineSaveForm} disabled={busy}>
                    Save
                  </button>
                </div>
              </div>

              <div className="adm-sec-form">
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                    <label className="adm-label">Section Title</label>
                    <input className="adm-input" value={doctorsTimelineForm.title || ''} onChange={(e) => setDoctorsTimelineForm((s) => ({ ...s, title: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                    <label className="adm-label">Tagline (short)</label>
                    <input className="adm-input" value={doctorsTimelineForm.tagline || ''} onChange={(e) => setDoctorsTimelineForm((s) => ({ ...s, tagline: e.target.value }))} />
                  </div>
                </div>

                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ width: 220 }}>
                    <label className="adm-label">Year Label</label>
                    <input className="adm-input" value={doctorsTimelineForm.yearLabel || ''} onChange={(e) => setDoctorsTimelineForm((s) => ({ ...s, yearLabel: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ width: 220 }}>
                    <label className="adm-label">Year</label>
                    <input className="adm-input" value={doctorsTimelineForm.yearOfEstablishment || ''} onChange={(e) => setDoctorsTimelineForm((s) => ({ ...s, yearOfEstablishment: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ width: 220 }}>
                    <label className="adm-label">Founders Label</label>
                    <input className="adm-input" value={doctorsTimelineForm.foundersLabel || ''} onChange={(e) => setDoctorsTimelineForm((s) => ({ ...s, foundersLabel: e.target.value }))} />
                  </div>
                </div>

                <div className="adm-field">
                  <label className="adm-label">Founders (one per line)</label>
                  <textarea className="adm-textarea" rows={4} value={doctorsTimelineForm.foundersText || ''} onChange={(e) => setDoctorsTimelineForm((s) => ({ ...s, foundersText: e.target.value }))} />
                </div>

                <div className="adm-divider" style={{ margin: '10px 0' }} />

                <div className="adm-list-title">Doctor Card Editor</div>
                {!selectedItem ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No doctor selected</div>
                    <div className="adm-empty-sub">Click any doctor on the left to edit.</div>
                  </div>
                ) : (
                  <>
                    <div className="adm-field">
                      <label className="adm-label">Doctor Name</label>
                      <textarea className="adm-textarea" rows={2} value={editForm.doctorName || ''} onChange={(e) => setEditForm((s) => ({ ...s, doctorName: e.target.value }))} />
                      <div className="adm-muted" style={{ marginTop: 6 }}>Tip: Use a new line to show two names on two lines.</div>
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Qualification / Subtitle</label>
                      <input className="adm-input" value={editForm.qualification || ''} onChange={(e) => setEditForm((s) => ({ ...s, qualification: e.target.value }))} />
                    </div>
                    <div className="adm-sec-form-row">
                      <div className="adm-field" style={{ width: 180 }}>
                        <label className="adm-label">Order</label>
                        <input className="adm-input" type="number" value={String(typeof editForm.position !== 'undefined' ? editForm.position : 0)} onChange={(e) => setEditForm((s) => ({ ...s, position: Number(e.target.value || 0) }))} />
                      </div>
                      <div className="adm-field" style={{ width: 180 }}>
                        <label className="adm-label">Status</label>
                        <select className="adm-select" value={String(editForm.status || 'active')} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="adm-field" style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <div className="adm-sec-actions">
                          <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                            Save Doctor
                          </button>
                          <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                            Remove Doctor
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'articles') {
      return (
        <div className="adm-sec">
          <div className="adm-empty">
            <div className="adm-empty-title">Blog / Articles</div>
            <div className="adm-empty-sub">This module is planned for the next update.</div>
          </div>
        </div>
      );
    }

    if (activeTab === 'testimonials') {
      const count = filteredTestimonials.length;
      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={testQuery}
                onChange={(e) => setTestQuery(e.target.value)}
                placeholder="Search testimonials (name, category, quote)…"
              />
              <select className="adm-select adm-sec-filter" value={testPublished} onChange={(e) => setTestPublished(e.target.value)}>
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button
                type="button"
                className="adm-btn"
                onClick={() => {
                  setTestQuery('');
                  setTestPublished('all');
                }}
                disabled={!String(testQuery || '').trim() && testPublished === 'all'}
              >
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add New
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Testimonials</div>

              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No testimonials</div>
                    <div className="adm-empty-sub">Create a new testimonial or change filters.</div>
                    <button type="button" className="adm-btn adm-btn--primary" onClick={openCreate}>
                      Add New
                    </button>
                  </div>
                ) : (
                  filteredTestimonials.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const isPublished = Boolean(it && it.isPublished);
                    const quote = String(it && it.quote ? it.quote : '');
                    const quoteShort = quote.length > 120 ? `${quote.slice(0, 120)}…` : quote;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{String(it && it.patientDisplayName ? it.patientDisplayName : '—')}</div>
                          <div className="adm-card-row-chips">
                            <span className={`adm-chip ${isPublished ? 'adm-chip--confirmed' : 'adm-chip--inactive'}`}>{isPublished ? 'published' : 'draft'}</span>
                          </div>
                        </div>
                        <div className="adm-card-row-sub">
                          <div className="adm-card-row-meta">{String(it && it.category ? it.category : '')}</div>
                          <div className="adm-card-row-meta">{String(it && it.age ? it.age : '') ? `Age: ${String(it.age)}` : ''}</div>
                        </div>
                        {quoteShort ? <div className="adm-card-row-msg">{quoteShort}</div> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Editor</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>{selectedItem ? String(selectedItem.id || '') : 'Select a testimonial'}</div>
                </div>
                {selectedItem ? (
                  <div className="adm-sec-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>

              {!selectedItem ? (
                <div className="adm-empty" style={{ marginTop: 10 }}>
                  <div className="adm-empty-title">No testimonial selected</div>
                  <div className="adm-empty-sub">Click any testimonial on the left to edit.</div>
                </div>
              ) : (
                <div className="adm-sec-form">
                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                      <label className="adm-label">Patient Display Name</label>
                      <input className="adm-input" value={editForm.patientDisplayName || ''} onChange={(e) => setEditForm((s) => ({ ...s, patientDisplayName: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ width: 160 }}>
                      <label className="adm-label">Age</label>
                      <input className="adm-input" value={editForm.age || ''} onChange={(e) => setEditForm((s) => ({ ...s, age: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Category</label>
                      <input className="adm-input" value={editForm.category || ''} onChange={(e) => setEditForm((s) => ({ ...s, category: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Quote</label>
                    <textarea className="adm-textarea" rows={5} value={editForm.quote || ''} onChange={(e) => setEditForm((s) => ({ ...s, quote: e.target.value }))} />
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                      <label className="adm-label">Outcome Label</label>
                      <input className="adm-input" value={editForm.outcomeLabel || ''} onChange={(e) => setEditForm((s) => ({ ...s, outcomeLabel: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                      <label className="adm-label">Treatment Duration</label>
                      <input className="adm-input" value={editForm.treatmentDuration || ''} onChange={(e) => setEditForm((s) => ({ ...s, treatmentDuration: e.target.value }))} />
                    </div>
                  </div>

                  <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={Boolean(editForm.isPublished)} onChange={(e) => setEditForm((s) => ({ ...s, isPublished: e.target.checked }))} />
                    Published
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'programs') {
      const count = filteredPrograms.length;
      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={progQuery}
                onChange={(e) => setProgQuery(e.target.value)}
                placeholder="Search programs (title)…"
              />
              <select className="adm-select adm-sec-filter" value={progPublished} onChange={(e) => setProgPublished(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button
                type="button"
                className="adm-btn"
                onClick={() => {
                  setProgQuery('');
                  setProgPublished('all');
                }}
                disabled={!String(progQuery || '').trim() && progPublished === 'all'}
              >
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add New
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Programs</div>

              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No programs</div>
                    <div className="adm-empty-sub">Create a new program or change filters.</div>
                    <button type="button" className="adm-btn adm-btn--primary" onClick={openCreate}>
                      Add New
                    </button>
                  </div>
                ) : (
                  filteredPrograms.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const st = String(it && it.status ? it.status : 'active');
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{String(it && it.title ? it.title : '—')}</div>
                          <div className="adm-card-row-chips">
                            <span className={`adm-chip ${st === 'active' ? 'adm-chip--confirmed' : 'adm-chip--inactive'}`}>{st === 'active' ? 'active' : 'inactive'}</span>
                          </div>
                        </div>
                        <div className="adm-card-row-sub">
                          <div className="adm-card-row-meta">Icon: {String(it && it.iconType ? it.iconType : '')}</div>
                          <div className="adm-card-row-meta">Order: {String(typeof it.displayOrder !== 'undefined' ? it.displayOrder : 0)}</div>
                        </div>
                        {it && it.shortDescription ? <div className="adm-card-row-msg">{String(it.shortDescription)}</div> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Editor</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>{selectedItem ? String(selectedItem.id || '') : 'Select a program'}</div>
                </div>
                {selectedItem ? (
                  <div className="adm-sec-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>

              {!selectedItem ? (
                <div className="adm-empty" style={{ marginTop: 10 }}>
                  <div className="adm-empty-title">No program selected</div>
                  <div className="adm-empty-sub">Click any program on the left to edit.</div>
                </div>
              ) : (
                <div className="adm-sec-form">
                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Program Name</label>
                      <input className="adm-input" value={editForm.title || ''} onChange={(e) => setEditForm((s) => ({ ...s, title: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Icon Style</label>
                      <input className="adm-input" value={editForm.iconType || ''} onChange={(e) => setEditForm((s) => ({ ...s, iconType: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Short Description</label>
                    <textarea className="adm-textarea" rows={3} value={editForm.shortDescription || ''} onChange={(e) => setEditForm((s) => ({ ...s, shortDescription: e.target.value }))} />
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Bullet Points (max 3, one per line)</label>
                    <textarea className="adm-textarea" rows={6} value={editForm.bulletsText || ''} onChange={(e) => setEditForm((s) => ({ ...s, bulletsText: e.target.value }))} />
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ width: 160 }}>
                      <label className="adm-label">Display Order</label>
                      <input className="adm-input" type="number" value={String(typeof editForm.displayOrder !== 'undefined' ? editForm.displayOrder : 0)} onChange={(e) => setEditForm((s) => ({ ...s, displayOrder: Number(e.target.value) }))} />
                    </div>
                    <div className="adm-field" style={{ width: 160 }}>
                      <label className="adm-label">Status</label>
                      <select className="adm-select" value={String(editForm.status || 'active')} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'feedback') {
      const count = filteredFeedback.length;

      const chipForStatus = (s) => {
        const raw = String(s || 'pending').toLowerCase();
        if (raw === 'pending') return 'new';
        if (raw === 'approved' || raw === 'published') return 'confirmed';
        if (raw === 'rejected' || raw === 'spam') return 'declined';
        if (raw === 'reviewed' || raw === 'archived') return 'completed';
        return 'new';
      };

      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={fbQuery}
                onChange={(e) => setFbQuery(e.target.value)}
                placeholder="Search feedback (name, message)…"
              />
              <select className="adm-select adm-sec-filter" value={fbStatus} onChange={(e) => setFbStatus(e.target.value)}>
                <option value="all">All status</option>
                <option value="pending">pending</option>
                <option value="reviewed">reviewed</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
              </select>
              <select className="adm-select adm-sec-filter" value={fbRating} onChange={(e) => setFbRating(e.target.value)}>
                <option value="all">All ratings</option>
                <option value="5">5</option>
                <option value="4">4</option>
                <option value="3">3</option>
                <option value="2">2</option>
                <option value="1">1</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button
                type="button"
                className="adm-btn"
                onClick={() => {
                  setFbQuery('');
                  setFbStatus('all');
                  setFbRating('all');
                }}
                disabled={!String(fbQuery || '').trim() && fbStatus === 'all' && fbRating === 'all'}
              >
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Feedback</div>

              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No feedback</div>
                    <div className="adm-empty-sub">Try changing filters or refresh the list.</div>
                  </div>
                ) : (
                  filteredFeedback.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const st = String(it && it.status ? it.status : 'pending');
                    const chip = chipForStatus(st);
                    const r = Number(it && typeof it.rating !== 'undefined' ? it.rating : 0);
                    const msg = String(it && it.message ? it.message : '');
                    const msgShort = msg.length > 90 ? `${msg.slice(0, 90)}…` : msg;

                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{String(it && it.name ? it.name : '—')}</div>
                          <div className="adm-card-row-chips">
                            <span className="adm-chip adm-chip--rating">{Number.isFinite(r) && r > 0 ? `${r}★` : '—'}</span>
                            <span className={`adm-chip adm-chip--${chip}`}>{st}</span>
                          </div>
                        </div>
                        {msgShort ? <div className="adm-card-row-msg">{msgShort}</div> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Details</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>{selectedItem ? String(selectedItem.id || '') : 'Select feedback'}</div>
                </div>
                {selectedItem ? (
                  <div className="adm-sec-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>

              {!selectedItem ? (
                <div className="adm-empty" style={{ marginTop: 10 }}>
                  <div className="adm-empty-title">No feedback selected</div>
                  <div className="adm-empty-sub">Click any item from the left list to view and update.</div>
                </div>
              ) : (
                <div className="adm-sec-form">
                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ width: 220 }}>
                      <label className="adm-label">Status</label>
                      <input className="adm-input" value={editForm.status || ''} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ width: 220 }}>
                      <label className="adm-label">Rating</label>
                      <select className="adm-select" value={String(editForm.rating || 5)} onChange={(e) => setEditForm((s) => ({ ...s, rating: Number(e.target.value) }))}>
                        <option value="5">5</option>
                        <option value="4">4</option>
                        <option value="3">3</option>
                        <option value="2">2</option>
                        <option value="1">1</option>
                      </select>
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Name</label>
                      <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Message</label>
                    <textarea className="adm-textarea" rows={5} value={editForm.message || ''} onChange={(e) => setEditForm((s) => ({ ...s, message: e.target.value }))} />
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Created At</label>
                      <input className="adm-input" value={String(selectedItem && (selectedItem.createdAt || selectedItem.create_at) ? selectedItem.createdAt || selectedItem.create_at : '')} disabled />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'adminUsers') {
      const count = filteredAdminUsers.length;

      const roleChip = (r) => {
        const raw = String(r || '').toLowerCase();
        if (raw === 'super_admin') return 'super_admin';
        if (raw === 'editor') return 'editor';
        if (raw === 'staff') return 'staff';
        return 'admin';
      };

      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={adminQuery}
                onChange={(e) => setAdminQuery(e.target.value)}
                placeholder="Search admin users (name, email)…"
              />
              <select className="adm-select adm-sec-filter" value={adminRole} onChange={(e) => setAdminRole(e.target.value)}>
                <option value="all">All roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="staff">Staff (legacy)</option>
              </select>
              <select className="adm-select adm-sec-filter" value={adminActive} onChange={(e) => setAdminActive(e.target.value)}>
                <option value="all">All status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button
                type="button"
                className="adm-btn"
                onClick={() => {
                  setAdminQuery('');
                  setAdminRole('all');
                  setAdminActive('all');
                }}
                disabled={!String(adminQuery || '').trim() && adminRole === 'all' && adminActive === 'all'}
              >
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add New
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Admin Users</div>

              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No admin users</div>
                    <div className="adm-empty-sub">Try changing filters or refresh the list.</div>
                  </div>
                ) : (
                  filteredAdminUsers.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const role = String(it && it.role ? it.role : 'admin');
                    const roleKey = roleChip(role);
                    const isActive = it && it.is_active !== false;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{String(it && it.name ? it.name : '—')}</div>
                          <div className="adm-card-row-chips">
                            <span className={`adm-chip adm-chip--${roleKey}`}>{role}</span>
                            <span className={`adm-chip ${isActive ? 'adm-chip--confirmed' : 'adm-chip--inactive'}`}>{isActive ? 'active' : 'inactive'}</span>
                          </div>
                        </div>
                        <div className="adm-card-row-sub">
                          <div className="adm-card-row-meta">{String(it && it.email ? it.email : '')}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Details</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>{selectedItem ? String(selectedItem.id || '') : 'Select an admin user'}</div>
                </div>
                {selectedItem ? (
                  <div className="adm-sec-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>

              {!selectedItem ? (
                <div className="adm-empty" style={{ marginTop: 10 }}>
                  <div className="adm-empty-title">No user selected</div>
                  <div className="adm-empty-sub">Click any user from the left list to view and update.</div>
                </div>
              ) : (
                <div className="adm-sec-form">
                  {String(editForm.role || '').trim().toLowerCase() === 'admin' ? (
                    <div className="adm-muted">Admins have full access. Permissions are mainly for legacy staff users.</div>
                  ) : null}

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Name</label>
                      <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Email</label>
                      <input className="adm-input" value={editForm.email || ''} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ width: 220 }}>
                      <label className="adm-label">Role</label>
                      <select className="adm-select" value={editForm.role || 'admin'} onChange={(e) => setEditForm((s) => ({ ...s, role: e.target.value }))}>
                        <option value="super_admin">super_admin</option>
                        <option value="admin">admin</option>
                        <option value="editor">editor</option>
                        <option value="staff">staff</option>
                      </select>
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Password (optional)</label>
                      <input className="adm-input" type="password" value={editForm.password || ''} onChange={(e) => setEditForm((s) => ({ ...s, password: e.target.value }))} placeholder="Leave blank to keep current" />
                    </div>
                  </div>

                  <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={Boolean(editForm.is_active)} onChange={(e) => setEditForm((s) => ({ ...s, is_active: e.target.checked }))} />
                    Active
                  </label>

                  <div className="adm-list-title" style={{ marginTop: 6 }}>Access Control</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {[
                      { key: 'appointments.manage', label: 'Appointments (manage)' },
                      { key: 'inquiries.manage', label: 'Inquiries (manage)' },
                      { key: 'feedback.manage', label: 'Feedback (manage)' },
                      { key: 'notifications.read', label: 'Notifications (read)' },
                      { key: 'settings.manage', label: 'Settings (manage)' },
                      { key: 'content.manage', label: 'Content (manage)' },
                      { key: 'adminUsers.manage', label: 'Admin Users (manage)' },
                    ].map((p) => {
                      const roleLower = String(editForm.role || '').trim().toLowerCase();
                      const disabled = roleLower === 'admin';
                      const perms = editForm && editForm.permissions && typeof editForm.permissions === 'object' ? editForm.permissions : {};
                      const checked = Boolean(perms && Object.prototype.hasOwnProperty.call(perms, p.key) ? perms[p.key] : false);

                      return (
                        <label key={p.key} className="adm-muted" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) =>
                              setEditForm((s) => ({
                                ...s,
                                permissions: {
                                  ...(s && s.permissions && typeof s.permissions === 'object' ? s.permissions : {}),
                                  [p.key]: e.target.checked,
                                },
                              }))
                            }
                          />
                          {p.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'inquiries') {
      const count = filteredInquiries.length;
      const chipFor = (s) => {
        const raw = String(s || 'new');
        if (raw === 'new') return 'new';
        if (raw === 'resolved' || raw === 'closed') return 'completed';
        if (raw === 'spam' || raw === 'deleted') return 'declined';
        if (raw === 'in_progress' || raw === 'in-progress' || raw === 'assigned') return 'confirmed';
        return 'new';
      };

      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={inqQuery}
                onChange={(e) => setInqQuery(e.target.value)}
                placeholder="Search inquiries (name, phone, email, message)…"
              />
              <select className="adm-select adm-sec-filter" value={inqStatus} onChange={(e) => setInqStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button type="button" className="adm-btn" onClick={() => { setInqQuery(''); setInqStatus('all'); }} disabled={!String(inqQuery || '').trim() && inqStatus === 'all'}>
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add New
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Contact Inquiries</div>

              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No inquiries</div>
                    <div className="adm-empty-sub">Try changing filters or refresh the list.</div>
                  </div>
                ) : (
                  filteredInquiries.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const st = String(it && it.status ? it.status : 'new');
                    const chip = chipFor(st);
                    const msg = String(it && it.message ? it.message : '');
                    const msgShort = msg.length > 90 ? `${msg.slice(0, 90)}…` : msg;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{String(it && it.name ? it.name : '—')}</div>
                          <span className={`adm-chip adm-chip--${chip}`}>{st}</span>
                        </div>
                        <div className="adm-card-row-sub">
                          <div className="adm-card-row-meta">{String(it && it.phone ? it.phone : '')}</div>
                          <div className="adm-card-row-meta">{String(it && it.email ? it.email : '')}</div>
                        </div>
                        {msgShort ? <div className="adm-card-row-msg">{msgShort}</div> : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Details</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>{selectedItem ? String(selectedItem.id || '') : 'Select an inquiry'}</div>
                </div>
                {selectedItem ? (
                  <div className="adm-sec-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>

              {!selectedItem ? (
                <div className="adm-empty" style={{ marginTop: 10 }}>
                  <div className="adm-empty-title">No inquiry selected</div>
                  <div className="adm-empty-sub">Click any inquiry from the left list to view and update.</div>
                </div>
              ) : (
                <div className="adm-sec-form">
                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ width: 220 }}>
                      <label className="adm-label">Status</label>
                      <input className="adm-input" value={editForm.status || ''} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Assigned To (User ID)</label>
                      <input className="adm-input" value={editForm.assigned_to_user_id || ''} onChange={(e) => setEditForm((s) => ({ ...s, assigned_to_user_id: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Name</label>
                      <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Email</label>
                      <input className="adm-input" value={editForm.email || ''} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Phone</label>
                      <input className="adm-input" value={editForm.phone || ''} onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Message</label>
                    <textarea className="adm-textarea" rows={4} value={editForm.message || ''} onChange={(e) => setEditForm((s) => ({ ...s, message: e.target.value }))} />
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Source</label>
                      <input className="adm-input" value={editForm.source || ''} onChange={(e) => setEditForm((s) => ({ ...s, source: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Created At</label>
                      <input className="adm-input" value={String(selectedItem && (selectedItem.created_at || selectedItem.create_at) ? selectedItem.created_at || selectedItem.create_at : '')} disabled />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'appointments') {
      const count = filteredAppointments.length;
      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div className="adm-sec-left">
              <input
                className="adm-input adm-sec-search"
                value={apptQuery}
                onChange={(e) => setApptQuery(e.target.value)}
                placeholder="Search appointments (name, phone, email)…"
              />
              <select className="adm-select adm-sec-filter" value={apptStatus} onChange={(e) => setApptStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="confirmed">Confirmed</option>
                <option value="declined">Declined</option>
                <option value="completed">Completed</option>
              </select>
              <div className="adm-muted" style={{ whiteSpace: 'nowrap' }}>{count} item(s)</div>
            </div>

            <div className="adm-sec-actions">
              <button type="button" className="adm-btn" onClick={() => { setApptQuery(''); setApptStatus('all'); }} disabled={!String(apptQuery || '').trim() && apptStatus === 'all'}>
                Clear
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add New
              </button>
            </div>
          </div>

          <div className="adm-sec-grid">
            <div className="adm-card adm-sec-list">
              <div className="adm-list-title">Appointment Requests</div>

              <div className="adm-card-list">
                {!count ? (
                  <div className="adm-empty" style={{ marginTop: 10 }}>
                    <div className="adm-empty-title">No appointments</div>
                    <div className="adm-empty-sub">Try changing filters or refresh the list.</div>
                  </div>
                ) : (
                  filteredAppointments.map((it) => {
                    const id = String(it && it.id ? it.id : '');
                    const st = String(it && it.status ? it.status : 'new');
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`adm-card-row${selectedId === id ? ' adm-card-row--active' : ''}`}
                        onClick={() => setSelectedId(id)}
                      >
                        <div className="adm-card-row-top">
                          <div className="adm-card-row-title">{String(it && it.name ? it.name : '—')}</div>
                          <span className={`adm-chip adm-chip--${st}`}>{st}</span>
                        </div>
                        <div className="adm-card-row-sub">
                          <div className="adm-card-row-meta">{String(it && it.phone ? it.phone : '')}</div>
                          <div className="adm-card-row-meta">{String(it && it.preferred_date ? it.preferred_date : '')}{it && it.preferred_time ? ` • ${String(it.preferred_time)}` : ''}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="adm-card adm-sec-detail">
              <div className="adm-sec-detail-head">
                <div>
                  <div className="adm-list-title">Details</div>
                  <div className="adm-muted" style={{ marginTop: 2 }}>{selectedItem ? String(selectedItem.id || '') : 'Select an appointment'}</div>
                </div>
                {selectedItem ? (
                  <div className="adm-sec-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>

              {!selectedItem ? (
                <div className="adm-empty" style={{ marginTop: 10 }}>
                  <div className="adm-empty-title">No appointment selected</div>
                  <div className="adm-empty-sub">Click any request from the left list to view and update.</div>
                </div>
              ) : (
                <div className="adm-sec-form">
                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ width: 220 }}>
                      <label className="adm-label">Status</label>
                      <select className="adm-select" value={editForm.status || 'new'} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))}>
                        <option value="new">new</option>
                        <option value="confirmed">confirmed</option>
                        <option value="declined">declined</option>
                        <option value="completed">completed</option>
                      </select>
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Name</label>
                      <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Phone</label>
                      <input className="adm-input" value={editForm.phone || ''} onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Email</label>
                      <input className="adm-input" value={editForm.email || ''} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Preferred Date</label>
                      <input className="adm-input" value={editForm.preferred_date || ''} onChange={(e) => setEditForm((s) => ({ ...s, preferred_date: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Preferred Time</label>
                      <input className="adm-input" value={editForm.preferred_time || ''} onChange={(e) => setEditForm((s) => ({ ...s, preferred_time: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Notes</label>
                    <textarea className="adm-textarea" rows={4} value={editForm.notes || ''} onChange={(e) => setEditForm((s) => ({ ...s, notes: e.target.value }))} />
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Confirmed Date</label>
                      <input className="adm-input" value={editForm.date || ''} onChange={(e) => setEditForm((s) => ({ ...s, date: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Confirmed Time</label>
                      <input className="adm-input" value={editForm.time || ''} onChange={(e) => setEditForm((s) => ({ ...s, time: e.target.value }))} />
                    </div>
                    <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                      <label className="adm-label">Reason</label>
                      <input className="adm-input" value={editForm.reason || ''} onChange={(e) => setEditForm((s) => ({ ...s, reason: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'settings') {
      const serverOnline = settingsBusy ? 'Loading…' : 'Online';
      const isImages = String(settingsQuery || '').toLowerCase().includes('image');
      const dbReadyState =
        settingsData && settingsData.db && typeof settingsData.db.readyState !== 'undefined'
          ? String(settingsData.db.readyState)
          : '-';
      const dbHost = settingsData && settingsData.db && settingsData.db.host ? String(settingsData.db.host) : '-';
      const uptimeText = settingsData && typeof settingsData.uptime !== 'undefined'
        ? `${Math.floor(Number(settingsData.uptime || 0))}s`
        : '-';

      const profileEmail =
        String(myProfile && myProfile.email ? myProfile.email : '').trim() ||
        String(tokenInfo && tokenInfo.email ? tokenInfo.email : '').trim() ||
        String(loginEmail || '').trim() ||
        '-';

      const profileDisplayName =
        String(myProfile && myProfile.name ? myProfile.name : '').trim() ||
        String(profileName || '').trim() ||
        'Admin';

      const profileDisplayRole =
        String(myProfile && myProfile.role ? myProfile.role : '').trim() ||
        String(profileRole || '').trim() ||
        'Admin';

      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div>
              <div className="adm-list-title">{isImages ? 'Images & Banners' : 'Settings'}</div>
              <div className="adm-muted" style={{ marginTop: 2 }}>{isImages ? 'Upload logo and website images' : 'System health & app configuration'}</div>
            </div>

            <div className="adm-sec-actions">
              <button
                type="button"
                className="adm-btn"
                onClick={() => {
                  loadSettings();
                  loadAppSettings();
                  loadMyProfile();
                }}
                disabled={settingsBusy || appSettingsBusy}
              >
                {settingsBusy || appSettingsBusy ? 'Loading…' : 'Refresh'}
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={openSettingsCreate}>
                Add Setting
              </button>
            </div>
          </div>

          {settingsError ? <div className="adm-error">{settingsError}</div> : null}
          {appSettingsError ? <div className="adm-error">{appSettingsError}</div> : null}
          {error ? <div className="adm-error">{error}</div> : null}
          {success ? <div className="adm-success">{success}</div> : null}

          <div className="adm-settings-grid">
            {isImages ? (
              <div className="adm-card adm-settings-card adm-settings-card--wide">
                <div className="adm-settings-card-head">
                  <div>
                    <div className="adm-settings-card-title">Website Brand Assets</div>
                    <div className="adm-settings-card-sub">Logo and main hero image (stored in Cloudinary)</div>
                  </div>
                  <button type="button" className="adm-btn" onClick={loadBrandAssets} disabled={busy}>
                    {busy ? 'Loading…' : 'Refresh'}
                  </button>
                </div>

                <div className="adm-sec-form" style={{ marginTop: 10 }}>
                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 260 }}>
                      <label className="adm-label">Website Logo</label>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ width: 64, height: 64, borderRadius: 12, overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {clinicProfile && clinicProfile.logoUrl ? (
                            <img src={String(clinicProfile.logoUrl)} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <div className="adm-muted">—</div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          disabled={busy}
                          onChange={async (e) => {
                            const f = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
                            if (!f) return;
                            const url = await uploadImageToCloudinary(f);
                            if (url) await setClinicLogoUrl(url);
                            try { e.target.value = ''; } catch (_) { /* ignore */ }
                          }}
                        />
                        <button type="button" className="adm-btn adm-btn--danger" onClick={() => setClinicLogoUrl('')} disabled={busy || !(clinicProfile && clinicProfile.logoUrl)}>
                          Remove
                        </button>
                      </div>
                      <div className="adm-muted" style={{ marginTop: 6 }}>{clinicProfile && clinicProfile.logoUrl ? String(clinicProfile.logoUrl) : 'No logo set yet'}</div>
                    </div>
                  </div>

                  <div className="adm-sec-form-row">
                    <div className="adm-field" style={{ flex: 1, minWidth: 260 }}>
                      <label className="adm-label">Hero / Doctor Image</label>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ width: 120, height: 70, borderRadius: 12, overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {homeContent && homeContent.heroImageUrl ? (
                            <img src={String(homeContent.heroImageUrl)} alt="Hero" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div className="adm-muted">—</div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          disabled={busy}
                          onChange={async (e) => {
                            const f = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
                            if (!f) return;
                            const url = await uploadImageToCloudinary(f);
                            if (url) await setHeroImageUrl(url);
                            try { e.target.value = ''; } catch (_) { /* ignore */ }
                          }}
                        />
                        <button type="button" className="adm-btn adm-btn--danger" onClick={() => setHeroImageUrl('')} disabled={busy || !(homeContent && homeContent.heroImageUrl)}>
                          Remove
                        </button>
                      </div>
                      <div className="adm-muted" style={{ marginTop: 6 }}>{homeContent && homeContent.heroImageUrl ? String(homeContent.heroImageUrl) : 'No hero image set yet'}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="adm-card adm-settings-card">
              <div className="adm-settings-card-head">
                <div>
                  <div className="adm-settings-card-title">System Health</div>
                  <div className="adm-settings-card-sub">Live server & database status</div>
                </div>
                <div className={`adm-pill ${settingsBusy ? 'adm-pill--muted' : 'adm-pill--ok'}`}>{serverOnline}</div>
              </div>

              <div className="adm-settings-metrics">
                <div className="adm-settings-metric">
                  <div className="adm-settings-metric-label">DB readyState</div>
                  <div className="adm-settings-metric-value">{dbReadyState}</div>
                </div>
                <div className="adm-settings-metric">
                  <div className="adm-settings-metric-label">DB host</div>
                  <div className="adm-settings-metric-value">{dbHost}</div>
                </div>
                <div className="adm-settings-metric">
                  <div className="adm-settings-metric-label">Uptime</div>
                  <div className="adm-settings-metric-value">{uptimeText}</div>
                </div>
              </div>
            </div>

            <div className="adm-card adm-settings-card adm-settings-card--wide">
              <div className="adm-settings-card-head">
                <div>
                  <div className="adm-settings-card-title">Profile Settings</div>
                  <div className="adm-settings-card-sub">Your admin account details</div>
                </div>
              </div>

              {myProfileError ? <div className="adm-error" style={{ marginBottom: 10 }}>{myProfileError}</div> : null}

              <div className="adm-profile-card">
                <div className="adm-profile-card-left">
                  <div className="adm-profile-card-avatar" aria-hidden="true">
                    {profileAvatarUrl ? (
                      <img className="adm-profile-card-avatar-img" src={profileAvatarUrl} alt="Profile" />
                    ) : (
                      profileInitials
                    )}
                  </div>
                  <div className="adm-profile-card-meta">
                    <div className="adm-profile-card-name">{profileDisplayName}{myProfileBusy ? ' (Loading…)': ''}</div>
                    <div className="adm-profile-card-sub">{profileEmail}</div>
                    <div className="adm-profile-card-role">{profileDisplayRole}</div>
                  </div>
                </div>

                <div className="adm-profile-card-actions">
                  <button type="button" className="adm-btn" onClick={openProfileEdit}>
                    Edit
                  </button>
                  <button type="button" className="adm-btn" onClick={() => setProfileOpen(true)}>
                    View
                  </button>
                </div>
              </div>
            </div>

            <div className="adm-card adm-settings-card">
              <div className="adm-settings-card-head">
                <div>
                  <div className="adm-settings-card-title">App Settings</div>
                  <div className="adm-settings-card-sub">Configuration key/value pairs</div>
                </div>
                <div className="adm-settings-count">{filteredAppSettings.length} item(s)</div>
              </div>

              <div className="adm-settings-toolbar">
                <input
                  className="adm-input adm-settings-search"
                  value={settingsQuery}
                  onChange={(e) => setSettingsQuery(e.target.value)}
                  placeholder="Search settings…"
                />
                <button type="button" className="adm-btn" onClick={() => setSettingsQuery('')} disabled={!String(settingsQuery || '').trim()}>
                  Clear
                </button>
              </div>

              <div className="adm-settings-crud">
                {appSettingsBusy ? <div className="adm-muted">Loading…</div> : null}

                {!appSettingsBusy && !filteredAppSettings.length ? (
                  <div className="adm-empty">
                    <div className="adm-empty-title">No settings found</div>
                    <div className="adm-empty-sub">Create your first configuration entry to manage app behavior.</div>
                    <button type="button" className="adm-btn adm-btn--primary" onClick={openSettingsCreate}>
                      Add Setting
                    </button>
                  </div>
                ) : null}

                {!appSettingsBusy && filteredAppSettings.length ? (
                  <div className="adm-settings-crud-list">
                    {filteredAppSettings.map((it) => (
                      <div key={String(it && it.id ? it.id : '')} className="adm-settings-crud-row">
                        <div className="adm-settings-crud-main">
                          <div className="adm-settings-crud-key">{String(it && it.key ? it.key : '')}</div>
                          {it && it.description ? <div className="adm-settings-crud-desc">{String(it.description)}</div> : null}
                          <div className="adm-settings-crud-val">
                            {(() => {
                              const v = String(toDisplayValue(it && typeof it.value !== 'undefined' ? it.value : ''));
                              return v.length > 120 ? `${v.slice(0, 120)}…` : v;
                            })()}
                          </div>
                        </div>
                        <div className="adm-settings-crud-actions">
                          <button type="button" className="adm-btn" onClick={() => openSettingsEdit(it)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="adm-btn adm-btn--danger"
                            onClick={() => deleteSettingsItem(String(it && it.id ? it.id : ''))}
                            disabled={appSettingsBusy}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'homeContent') {
      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div>
              <div className="adm-list-title">Home Content</div>
              <div className="adm-muted" style={{ marginTop: 2 }}>Website homepage sections</div>
            </div>

            <div className="adm-sec-actions">
              <button type="button" className="adm-btn" onClick={() => loadHomeContent()} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={handleHomeContentSaveForm} disabled={busy}>
                Save
              </button>
              <button type="button" className="adm-btn adm-btn--danger" onClick={handleHomeContentReset} disabled={busy}>
                Reset
              </button>
            </div>
          </div>

          <div className="adm-form-grid">
            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Hero</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Hero Title Lines (one per line)</label>
                  <textarea
                    className="adm-textarea"
                    rows={4}
                    value={homeForm.heroTitleLinesText}
                    onChange={(e) => setHomeForm((s) => ({ ...s, heroTitleLinesText: e.target.value }))}
                  />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Hero Subtitle</label>
                  <textarea
                    className="adm-textarea"
                    rows={3}
                    value={homeForm.heroSubtitle}
                    onChange={(e) => setHomeForm((s) => ({ ...s, heroSubtitle: e.target.value }))}
                  />
                </div>
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">CTA Primary</label>
                    <input className="adm-input" value={homeForm.ctaPrimary} onChange={(e) => setHomeForm((s) => ({ ...s, ctaPrimary: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">CTA Secondary</label>
                    <input className="adm-input" value={homeForm.ctaSecondary} onChange={(e) => setHomeForm((s) => ({ ...s, ctaSecondary: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Hero Tag Title</label>
                    <input className="adm-input" value={homeForm.heroTagTitle} onChange={(e) => setHomeForm((s) => ({ ...s, heroTagTitle: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Hero Tag Subtitle</label>
                    <input className="adm-input" value={homeForm.heroTagSubtitle} onChange={(e) => setHomeForm((s) => ({ ...s, heroTagSubtitle: e.target.value }))} />
                  </div>
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Hero Stats</div>
              <div className="adm-form-body">
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ width: 160 }}>
                    <label className="adm-label">Stat 1 Value</label>
                    <input className="adm-input" value={homeForm.stat1Value} onChange={(e) => setHomeForm((s) => ({ ...s, stat1Value: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Stat 1 Label</label>
                    <input className="adm-input" value={homeForm.stat1Label} onChange={(e) => setHomeForm((s) => ({ ...s, stat1Label: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ width: 160 }}>
                    <label className="adm-label">Stat 2 Value</label>
                    <input className="adm-input" value={homeForm.stat2Value} onChange={(e) => setHomeForm((s) => ({ ...s, stat2Value: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Stat 2 Label</label>
                    <input className="adm-input" value={homeForm.stat2Label} onChange={(e) => setHomeForm((s) => ({ ...s, stat2Label: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ width: 160 }}>
                    <label className="adm-label">Stat 3 Value</label>
                    <input className="adm-input" value={homeForm.stat3Value} onChange={(e) => setHomeForm((s) => ({ ...s, stat3Value: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Stat 3 Label</label>
                    <input className="adm-input" value={homeForm.stat3Label} onChange={(e) => setHomeForm((s) => ({ ...s, stat3Label: e.target.value }))} />
                  </div>
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Legacy Section</div>
              <div className="adm-form-body">
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Title Prefix</label>
                    <input className="adm-input" value={homeForm.legacyTitlePrefix} onChange={(e) => setHomeForm((s) => ({ ...s, legacyTitlePrefix: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Title Highlight</label>
                    <input className="adm-input" value={homeForm.legacyTitleHighlight} onChange={(e) => setHomeForm((s) => ({ ...s, legacyTitleHighlight: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-field">
                  <label className="adm-label">Legacy Subtitle</label>
                  <textarea
                    className="adm-textarea"
                    rows={3}
                    value={homeForm.legacySubtitle}
                    onChange={(e) => setHomeForm((s) => ({ ...s, legacySubtitle: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Experience Section</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Experience Title</label>
                  <input className="adm-input" value={homeForm.experienceTitle} onChange={(e) => setHomeForm((s) => ({ ...s, experienceTitle: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Experience Quote</label>
                  <textarea
                    className="adm-textarea"
                    rows={4}
                    value={homeForm.experienceQuote}
                    onChange={(e) => setHomeForm((s) => ({ ...s, experienceQuote: e.target.value }))}
                  />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Experience Author</label>
                  <input className="adm-input" value={homeForm.experienceAuthor} onChange={(e) => setHomeForm((s) => ({ ...s, experienceAuthor: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'clinicProfile') {
      return (
        <div className="adm-sec">
          <div className="adm-sec-toolbar">
            <div>
              <div className="adm-list-title">Clinic Profile</div>
              <div className="adm-muted" style={{ marginTop: 2 }}>Public clinic details shown on website</div>
            </div>

            <div className="adm-sec-actions">
              <button type="button" className="adm-btn" onClick={() => loadClinicProfile()} disabled={busy}>
                Refresh
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={handleClinicProfileSaveForm} disabled={busy}>
                Save
              </button>
              <button type="button" className="adm-btn adm-btn--danger" onClick={handleClinicProfileDelete} disabled={busy}>
                Delete
              </button>
            </div>
          </div>

          <div className="adm-form-grid">
            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Basic Info</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Clinic Name *</label>
                  <input className="adm-input" value={clinicForm.name} onChange={(e) => setClinicForm((s) => ({ ...s, name: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Tagline *</label>
                  <input className="adm-input" value={clinicForm.tagline} onChange={(e) => setClinicForm((s) => ({ ...s, tagline: e.target.value }))} />
                </div>
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Established Years (e.g. 40 yrs)</label>
                    <input className="adm-input" value={clinicForm.establishedYears} onChange={(e) => setClinicForm((s) => ({ ...s, establishedYears: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Established Year (e.g. 1975)</label>
                    <input className="adm-input" value={clinicForm.establishedYear} onChange={(e) => setClinicForm((s) => ({ ...s, establishedYear: e.target.value }))} />
                  </div>
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Doctors</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Founder(s) (one per line)</label>
                  <textarea
                    className="adm-textarea"
                    rows={3}
                    value={clinicForm.foundersText}
                    onChange={(e) => setClinicForm((s) => ({ ...s, foundersText: e.target.value }))}
                  />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Doctors Working (one per line)</label>
                  <textarea
                    className="adm-textarea"
                    rows={3}
                    value={clinicForm.workingDoctorsText}
                    onChange={(e) => setClinicForm((s) => ({ ...s, workingDoctorsText: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Specialization</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Specializations (one per line)</label>
                  <textarea
                    className="adm-textarea"
                    rows={6}
                    value={clinicForm.specializationsText}
                    onChange={(e) => setClinicForm((s) => ({ ...s, specializationsText: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Contact</div>
              <div className="adm-form-body">
                <div className="adm-sec-form-row">
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Primary Phone *</label>
                    <input className="adm-input" value={clinicForm.primaryPhone} onChange={(e) => setClinicForm((s) => ({ ...s, primaryPhone: e.target.value }))} />
                  </div>
                  <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                    <label className="adm-label">Secondary Phone</label>
                    <input className="adm-input" value={clinicForm.secondaryPhone} onChange={(e) => setClinicForm((s) => ({ ...s, secondaryPhone: e.target.value }))} />
                  </div>
                </div>
                <div className="adm-field">
                  <label className="adm-label">WhatsApp Number</label>
                  <input className="adm-input" value={clinicForm.whatsappNumber} onChange={(e) => setClinicForm((s) => ({ ...s, whatsappNumber: e.target.value }))} />
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Address & Hours</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Address *</label>
                  <textarea className="adm-textarea" rows={3} value={clinicForm.addressText} onChange={(e) => setClinicForm((s) => ({ ...s, addressText: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Hours *</label>
                  <textarea className="adm-textarea" rows={3} value={clinicForm.hoursText} onChange={(e) => setClinicForm((s) => ({ ...s, hoursText: e.target.value }))} />
                </div>
              </div>
            </div>

            <div className="adm-card adm-form-card">
              <div className="adm-list-title">Links</div>
              <div className="adm-form-body">
                <div className="adm-field">
                  <label className="adm-label">Google Maps URL</label>
                  <input className="adm-input" value={clinicForm.googleMapsUrl} onChange={(e) => setClinicForm((s) => ({ ...s, googleMapsUrl: e.target.value }))} />
                </div>
                <div className="adm-muted">Fields marked * are required.</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const title =
      activeTab === 'appointments'
        ? 'Appointment Requests'
        : activeTab === 'inquiries'
          ? 'Contact Inquiries'
          : activeTab === 'adminUsers'
            ? 'Admin Users'
            : activeTab === 'feedback'
              ? 'Feedback'
              : activeTab === 'programs'
                ? 'Programs'
                : 'Testimonials';

    return (
      <div className="adm-grid">
        <div className="adm-card adm-list">
          <div className="adm-list-title">{title}</div>
          <div className="adm-actions" style={{ marginBottom: 10 }}>
            <button type="button" className="adm-btn adm-btn--primary" onClick={refresh} disabled={busy}>
              Refresh
            </button>
            {canCreateWithForm ? (
              <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy}>
                Add New
              </button>
            ) : null}
            {showAdvanced ? (
              <button type="button" className="adm-btn" onClick={() => setPayloadText('{}')} disabled={busy}>
                New JSON
              </button>
            ) : null}
          </div>

          <div className="adm-list-items">
            {items.map((it) => {
              const id = String(it && it.id ? it.id : '');
              const subtitle =
                activeTab === 'appointments'
                  ? `${it.status || 'new'} • ${it.name || ''}`
                  : activeTab === 'inquiries'
                    ? `${it.status || 'new'} • ${it.name || ''}`
                    : activeTab === 'adminUsers'
                      ? `${it.role || ''} • ${it.email || ''}`
                      : activeTab === 'feedback'
                        ? `${it.status || ''} • ${it.name || ''} (${it.rating || ''})`
                        : activeTab === 'programs'
                          ? `${it.slug || ''} • ${it.title || ''}`
                          : `${it.patientDisplayName || ''} • ${it.category || ''}`;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedId(id)}
                  className={`adm-item${selectedId === id ? ' adm-item--active' : ''}`}
                >
                  <div className="adm-item-id">{id}</div>
                  <div className="adm-item-sub">{subtitle}</div>
                </button>
              );
            })}

            {!items.length ? <div className="adm-muted">No data</div> : null}
          </div>
        </div>

        <div className="adm-card adm-editor">
          <div className="adm-editor-head">
            <div className="adm-list-title">Details</div>
            <div className="adm-actions">
              {(activeTab === 'appointments' || activeTab === 'inquiries' || activeTab === 'adminUsers' || activeTab === 'feedback' || activeTab === 'programs' || activeTab === 'testimonials') &&
              selectedId ? (
                <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveForm} disabled={busy}>
                  Save
                </button>
              ) : null}
              <button type="button" className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={busy}>
                Delete
              </button>
              <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />
                Advanced
              </label>
            </div>
          </div>

          <div className="adm-section" style={{ display: 'grid', gap: 8 }}>
            <div className="adm-muted">Selected ID: {selectedId || '(none)'}</div>

            {(activeTab === 'appointments' || activeTab === 'inquiries' || activeTab === 'adminUsers' || activeTab === 'feedback' || activeTab === 'programs' || activeTab === 'testimonials') &&
            selectedItem ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {activeTab === 'appointments' ? (
                  <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ width: 200 }}>
                        <label className="adm-label">Status</label>
                        <select className="adm-select" value={editForm.status || 'new'} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))}>
                          <option value="new">new</option>
                          <option value="confirmed">confirmed</option>
                          <option value="declined">declined</option>
                          <option value="completed">completed</option>
                        </select>
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Name</label>
                        <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Phone</label>
                        <input className="adm-input" value={editForm.phone || ''} onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Email</label>
                        <input className="adm-input" value={editForm.email || ''} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Preferred Date</label>
                        <input className="adm-input" value={editForm.preferred_date || ''} onChange={(e) => setEditForm((s) => ({ ...s, preferred_date: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Preferred Time</label>
                        <input className="adm-input" value={editForm.preferred_time || ''} onChange={(e) => setEditForm((s) => ({ ...s, preferred_time: e.target.value }))} />
                      </div>
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Notes</label>
                      <textarea className="adm-textarea" rows={4} value={editForm.notes || ''} onChange={(e) => setEditForm((s) => ({ ...s, notes: e.target.value }))} />
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Confirmed Date</label>
                        <input className="adm-input" value={editForm.date || ''} onChange={(e) => setEditForm((s) => ({ ...s, date: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Confirmed Time</label>
                        <input className="adm-input" value={editForm.time || ''} onChange={(e) => setEditForm((s) => ({ ...s, time: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Reason</label>
                        <input className="adm-input" value={editForm.reason || ''} onChange={(e) => setEditForm((s) => ({ ...s, reason: e.target.value }))} />
                      </div>
                    </div>
                  </>
                ) : null}

                {activeTab === 'inquiries' ? (
                  <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ width: 200 }}>
                        <label className="adm-label">Status</label>
                        <input className="adm-input" value={editForm.status || ''} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Assigned To (User ID)</label>
                        <input className="adm-input" value={editForm.assigned_to_user_id || ''} onChange={(e) => setEditForm((s) => ({ ...s, assigned_to_user_id: e.target.value }))} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Name</label>
                        <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Email</label>
                        <input className="adm-input" value={editForm.email || ''} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Phone</label>
                        <input className="adm-input" value={editForm.phone || ''} onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))} />
                      </div>
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Message</label>
                      <textarea className="adm-textarea" rows={6} value={editForm.message || ''} onChange={(e) => setEditForm((s) => ({ ...s, message: e.target.value }))} />
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Source</label>
                      <input className="adm-input" value={editForm.source || ''} onChange={(e) => setEditForm((s) => ({ ...s, source: e.target.value }))} />
                    </div>
                  </>
                ) : null}

                {activeTab === 'adminUsers' ? (
                  <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Name</label>
                        <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Email</label>
                        <input className="adm-input" value={editForm.email || ''} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ width: 200 }}>
                        <label className="adm-label">Role</label>
                        <select className="adm-select" value={editForm.role || 'admin'} onChange={(e) => setEditForm((s) => ({ ...s, role: e.target.value }))}>
                          <option value="super_admin">super_admin</option>
                          <option value="admin">admin</option>
                          <option value="editor">editor</option>
                          <option value="staff">staff</option>
                        </select>
                      </div>
                      <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 24 }}>
                        <input type="checkbox" checked={Boolean(editForm.is_active)} onChange={(e) => setEditForm((s) => ({ ...s, is_active: e.target.checked }))} />
                        Active
                      </label>
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">New Password (optional)</label>
                      <input type="password" className="adm-input" value={editForm.password || ''} onChange={(e) => setEditForm((s) => ({ ...s, password: e.target.value }))} />
                    </div>
                  </>
                ) : null}

                {activeTab === 'feedback' ? (
                  <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ width: 220 }}>
                        <label className="adm-label">Status</label>
                        <input className="adm-input" value={editForm.status || ''} onChange={(e) => setEditForm((s) => ({ ...s, status: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Name</label>
                        <input className="adm-input" value={editForm.name || ''} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ width: 160 }}>
                        <label className="adm-label">Rating</label>
                        <input type="number" min={1} max={5} className="adm-input" value={String(editForm.rating ?? '')} onChange={(e) => setEditForm((s) => ({ ...s, rating: e.target.value }))} />
                      </div>
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Message</label>
                      <textarea className="adm-textarea" rows={6} value={editForm.message || ''} onChange={(e) => setEditForm((s) => ({ ...s, message: e.target.value }))} />
                    </div>
                  </>
                ) : null}

                {activeTab === 'programs' ? (
                  <>
                    <div className="adm-field">
                      <label className="adm-label">Title</label>
                      <input
                        className="adm-input"
                        value={editForm.title || ''}
                        onChange={(e) => {
                          const title = e.target.value;
                          setEditForm((s) => ({ ...s, title, slug: s.slug ? s.slug : toSlug(title) }));
                        }}
                      />
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Slug</label>
                      <input className="adm-input" value={editForm.slug || ''} onChange={(e) => setEditForm((s) => ({ ...s, slug: toSlug(e.target.value) }))} />
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Short Description</label>
                      <textarea className="adm-textarea" rows={3} value={editForm.shortDescription || ''} onChange={(e) => setEditForm((s) => ({ ...s, shortDescription: e.target.value }))} />
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Bullets (one per line)</label>
                      <textarea className="adm-textarea" rows={5} value={editForm.bulletsText || ''} onChange={(e) => setEditForm((s) => ({ ...s, bulletsText: e.target.value }))} />
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Icon Key</label>
                        <input className="adm-input" value={editForm.iconKey || ''} onChange={(e) => setEditForm((s) => ({ ...s, iconKey: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ width: 160 }}>
                        <label className="adm-label">Sort Order</label>
                        <input type="number" className="adm-input" value={String(editForm.sortOrder ?? 0)} onChange={(e) => setEditForm((s) => ({ ...s, sortOrder: Number(e.target.value || 0) }))} />
                      </div>
                      <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 24 }}>
                        <input type="checkbox" checked={Boolean(editForm.isPublished)} onChange={(e) => setEditForm((s) => ({ ...s, isPublished: e.target.checked }))} />
                        Published
                      </label>
                    </div>
                  </>
                ) : null}

                {activeTab === 'testimonials' ? (
                  <>
                    <div className="adm-field">
                      <label className="adm-label">Patient Name</label>
                      <input className="adm-input" value={editForm.patientDisplayName || ''} onChange={(e) => setEditForm((s) => ({ ...s, patientDisplayName: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Category</label>
                        <input className="adm-input" value={editForm.category || ''} onChange={(e) => setEditForm((s) => ({ ...s, category: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ width: 160 }}>
                        <label className="adm-label">Age</label>
                        <input className="adm-input" value={String(editForm.age || '')} onChange={(e) => setEditForm((s) => ({ ...s, age: e.target.value }))} />
                      </div>
                      <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 24 }}>
                        <input type="checkbox" checked={Boolean(editForm.isPublished)} onChange={(e) => setEditForm((s) => ({ ...s, isPublished: e.target.checked }))} />
                        Published
                      </label>
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Quote</label>
                      <textarea className="adm-textarea" rows={5} value={editForm.quote || ''} onChange={(e) => setEditForm((s) => ({ ...s, quote: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Outcome Label</label>
                        <input className="adm-input" value={editForm.outcomeLabel || ''} onChange={(e) => setEditForm((s) => ({ ...s, outcomeLabel: e.target.value }))} />
                      </div>
                      <div className="adm-field" style={{ flex: 1, minWidth: 220 }}>
                        <label className="adm-label">Treatment Duration</label>
                        <input className="adm-input" value={editForm.treatmentDuration || ''} onChange={(e) => setEditForm((s) => ({ ...s, treatmentDuration: e.target.value }))} />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              selectedItem ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  {getDetailFields(activeTab, selectedItem).map((row) => (
                    <div key={row.label} style={{ display: 'grid', gap: 4 }}>
                      <div className="adm-muted" style={{ fontSize: 12, fontWeight: 800 }}>
                        {row.label}
                      </div>
                      <div style={{ padding: '10px 12px', border: '1px solid var(--adm-border)', borderRadius: 12, background: '#ffffff' }}>
                        {row.value}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="adm-muted">Select an item to view details.</div>
              )
            )}

            {showAdvanced ? (
              <>
                <div className="adm-actions" style={{ marginTop: 10 }}>
                  <button type="button" className="adm-btn adm-btn--success" onClick={openCreate} disabled={busy || !canCreateWithForm}>
                    Add New (Form)
                  </button>
                  <button type="button" className="adm-btn" onClick={handleCreate} disabled={busy}>
                    Create (JSON)
                  </button>
                  <button type="button" className="adm-btn adm-btn--primary" onClick={handlePatch} disabled={busy}>
                    Update (PATCH)
                  </button>
                </div>
                <textarea
                  value={payloadText}
                  onChange={(e) => setPayloadText(e.target.value)}
                  rows={16}
                  className="adm-textarea adm-code"
                />
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="adm-root">
      {!token ? (
        <div className="adm-login">
          <div className="adm-login-split">
            <div className="adm-login-left">
              <div className="adm-login-left-inner">
                <div className="adm-login-left-logo">
                  <img className="adm-login-left-logo-img" src="/aryalogo-transparent.png" alt="Arya Homoeo Hall" />
                </div>
                <div className="adm-login-left-title">Arya Homoeo Hall</div>
                <div className="adm-login-left-sub">Natural Healing Admin</div>
                <div className="adm-login-left-trust">Trusted Homeopathy Care • Since 1985</div>
              </div>
            </div>

            <div className="adm-login-right">
              <div className="adm-login-right-inner">
                <div className="adm-login-formcard">
                  <div className="adm-login-form-title">Welcome</div>
                  <div className="adm-login-form-sub">Please login to admin dashboard</div>

                  <div className="adm-login-form">
                    <div className="adm-field">
                      <label className="adm-label">Email / Username</label>
                      <input
                        className="adm-input"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="admin@example.com"
                        autoComplete="username"
                      />
                    </div>

                    <div className="adm-field">
                      <label className="adm-label">Password</label>
                      <div className="adm-login-pass-row">
                        <input
                          className="adm-input"
                          type={showLoginPassword ? 'text' : 'password'}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="Password"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="adm-login-eye"
                          onClick={() => setShowLoginPassword((s) => !s)}
                          aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                          title={showLoginPassword ? 'Hide password' : 'Show password'}
                        >
                          {showLoginPassword ? (
                            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                              <path
                                d="M12 5c-7 0-10 7-10 7s1.7 4 6 6.2L6.3 20 7.7 21.4l1.9-1.9c.8.2 1.6.3 2.4.3 7 0 10-7 10-7s-3-7-10-7Zm0 12c-.4 0-.8 0-1.1-.1l1.5-1.5c.6-.1 1.1-.4 1.5-.8.4-.4.6-1 .6-1.6 0-.2 0-.5-.1-.7l1.5-1.5c1.9 1.4 3 3.2 3 3.2s-2.6 5-7 5Zm-5.3-3.1c-1.9-1.4-3-3.2-3-3.2s2.6-5 7-5c.4 0 .8 0 1.1.1L10.3 7.3c-.6.1-1.1.4-1.5.8-.4.4-.6 1-.6 1.6 0 .2 0 .5.1.7l-1.6 1.5Zm3.3-2.7c0-.6.5-1.2 1.2-1.2.2 0 .4 0 .6.1l-1.7 1.7c-.1-.2-.1-.4-.1-.6Zm4.2 1.6c0 .6-.5 1.2-1.2 1.2-.2 0-.4 0-.6-.1l1.7-1.7c.1.2.1.4.1.6Z"
                                fill="currentColor"
                              />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                              <path
                                d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7Zm0 12c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5Zm0-8c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3-1.3-3-3-3Z"
                                fill="currentColor"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <button type="button" className="adm-login-submit" onClick={onLogin} disabled={busy}>
                      {busy ? 'Logging in…' : 'Login'}
                    </button>

                    <a
                      href="#"
                      className="adm-login-forgot"
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      Forgotten your password?
                    </a>

                    {error ? <div className="adm-error">{error}</div> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="adm-container">
          <div className={`adm-shell${sidebarCollapsed ? ' adm-shell--collapsed' : ''}`}>
            <aside className="adm-card adm-sidebar">
              <div className="adm-sidebar-top">
                <div className="adm-brand">
                  <img
                    className="adm-brand-logo"
                    src="/aryalogo-transparent.png"
                    alt="Arya"
                    onError={(e) => {
                      try {
                        e.currentTarget.style.display = 'none';
                      } catch (_) {
                        // ignore
                      }
                    }}
                  />
                  <div className="adm-brand-text">Arya Homoeo Hall - Admin</div>
                </div>

                <div className="adm-sidebar-search">
                  <input
                    className="adm-input adm-sidebar-search-input"
                    value={sidebarQuery}
                    onChange={(e) => setSidebarQuery(e.target.value)}
                    placeholder="Search menu…"
                  />
                  {String(sidebarQuery || '').trim() ? (
                    <button
                      type="button"
                      className="adm-sidebar-search-clear"
                      onClick={() => setSidebarQuery('')}
                      aria-label="Clear search"
                      title="Clear"
                    >
                      ×
                    </button>
                  ) : null}
                </div>

                <nav className="adm-nav">
                  {filteredSidebarGroups.map((g, idx) => (
                    <div key={String(g && g.header ? g.header : `group-${idx}`)} className="adm-nav-group">
                      {g && g.header ? <div className="adm-nav-group-title">{g.header}</div> : null}
                      {(g && Array.isArray(g.items) ? g.items : []).map((it) => {
                        const isAppointments = it && it.tab === 'appointments';
                        const isSettings = it && it.tab === 'settings';
                        const isActive =
                          (it && it.tab === activeTab && !isAppointments && !isSettings) ||
                          (isAppointments && it.status && activeTab === 'appointments' && String(apptStatus) === String(it.status)) ||
                          (isSettings && it.key === 'images' && activeTab === 'settings' && String(settingsQuery || '').toLowerCase().includes('image')) ||
                          (isSettings && it.key === 'seo' && activeTab === 'settings' && String(settingsQuery || '').toLowerCase().includes('seo')) ||
                          (isSettings && it.key === 'settings' && activeTab === 'settings' && !String(settingsQuery || '').trim());

                        const badgeValue =
                          it && it.tab === 'inquiries'
                            ? Number(dashboardStats.inquiryNew || 0)
                            : it && it.tab === 'appointments' && String(it.status || '') === 'new'
                              ? Number(dashboardStats.apptNew || 0)
                              : 0;

                        return (
                          <button
                            key={String(it && it.key ? `${it.tab}-${it.key}` : it && it.label ? `${it.tab}-${it.label}` : it && it.tab ? it.tab : 'item')}
                            type="button"
                            className={`adm-nav-item${isActive ? ' adm-nav-item--active' : ''}`}
                            onClick={it.onClick}
                          >
                            <span className="adm-nav-ico" aria-hidden="true">{it.icon}</span>
                            <span className="adm-nav-label">
                              <span className="adm-nav-label-main">{it.label}</span>
                              {it.sub ? <span className="adm-nav-label-sub">{it.sub}</span> : null}
                            </span>
                            {badgeValue > 0 ? (
                              <span className="adm-nav-badge">{badgeValue > 99 ? '99+' : String(badgeValue)}</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </nav>
              </div>

              <div className="adm-sidebar-bottom">
                <button type="button" className="adm-sidebar-logout" onClick={onLogout}>
                  Logout
                </button>
              </div>
            </aside>

            <main className="adm-card adm-main">
              <div className="adm-headerbar">
                <div className="adm-headerbar-left">
                  <button
                    type="button"
                    className="adm-icon-btn"
                    title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    onClick={() => setSidebarCollapsed((s) => !s)}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" fill="currentColor" />
                    </svg>
                  </button>
                  <img
                    className="adm-headerbar-logo"
                    src="/aryalogo-transparent.png"
                    alt="Arya"
                    onError={(e) => {
                      try {
                        e.currentTarget.style.display = 'none';
                      } catch (_) {
                        // ignore
                      }
                    }}
                  />
                  <div className="adm-headerbar-title">Arya Homoeo Hall</div>
                </div>
                <div className="adm-headerbar-actions">
                  <div className="adm-notif-wrap" ref={notifWrapRef}>
                    <button
                      type="button"
                      className="adm-icon-btn"
                      title="Notifications"
                      onClick={openNotifications}
                    >
                      {notificationsCount > 0 ? (
                        <span className="adm-notif-badge">{notificationsCount > 99 ? '99+' : String(notificationsCount)}</span>
                      ) : null}
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path
                          d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5-6.71V3a2 2 0 0 0-4 0v1.29A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>

                    {notificationsOpen ? (
                      <div className="adm-notif-popover" role="dialog" aria-label="Notifications">
                        <div className="adm-notif-popover-head">
                          <div className="adm-notif-popover-title">Notifications</div>
                          <div className="adm-notif-popover-actions">
                            <button type="button" className="adm-icon-btn" title="Refresh" onClick={loadNotifications} disabled={notificationsBusy}>
                              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                <path
                                  d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 13.65-6.65Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="adm-notif-popover-sub">
                          <div className="adm-muted">
                            {notificationsBusy
                              ? 'Loading…'
                              : notificationsData && notificationsData.counts
                                ? `New: ${Number(notificationsData.counts.totalNew || 0)}`
                                : ''}
                          </div>
                        </div>

                        {notificationsError ? <div className="adm-error" style={{ marginTop: 10 }}>{notificationsError}</div> : null}

                        <div className="adm-notif-list">
                          {(notificationsData && Array.isArray(notificationsData.items) ? notificationsData.items : []).length ? (
                            (notificationsData.items || []).map((it) => (
                              <button
                                key={`${it.type}-${it.id}`}
                                type="button"
                                className="adm-notif-item"
                                onClick={() => {
                                  setNotificationsOpen(false);
                                  if (it && it.tab) {
                                    setPendingSelection({ tab: String(it.tab), id: String(it.id || '') });
                                    setActiveTab(String(it.tab));
                                  }
                                }}
                              >
                                <div className="adm-notif-title">{String(it && it.title ? it.title : '')}</div>
                                <div className="adm-notif-sub">{String(it && it.subtitle ? it.subtitle : '')}</div>
                              </button>
                            ))
                          ) : (
                            <div className="adm-muted" style={{ padding: 10 }}>No new notifications</div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="adm-settings-wrap" ref={settingsWrapRef}>
                    <button type="button" className="adm-icon-btn" title="Settings" onClick={openSettings}>
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path
                          d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.06 7.06 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 1h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.7 7.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.3.6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54c.04.24.25.42.49.42h3.8c.24 0 .45-.18.49-.42l.36-2.54c.58-.23 1.12-.54 1.63-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>

                    {settingsOpen ? (
                      <div className="adm-settings-popover" role="dialog" aria-label="Settings">
                        <div className="adm-settings-popover-head">
                          <div className="adm-settings-popover-title">Settings</div>
                          <div className="adm-settings-popover-actions">
                            <button type="button" className="adm-icon-btn" title="Refresh" onClick={loadSettings} disabled={settingsBusy}>
                              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                <path
                                  d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 13.65-6.65Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {settingsError ? <div className="adm-error" style={{ marginTop: 10 }}>{settingsError}</div> : null}

                        <div className="adm-settings-section">
                          <div className="adm-settings-row">
                            <div className="adm-muted">Server</div>
                            <div style={{ fontWeight: 500 }}>{settingsBusy ? 'Loading…' : 'Online'}</div>
                          </div>
                          <div className="adm-settings-row">
                            <div className="adm-muted">DB</div>
                            <div style={{ fontWeight: 500 }}>
                              {settingsData && settingsData.db && typeof settingsData.db.readyState !== 'undefined'
                                ? String(settingsData.db.readyState)
                                : '-'}
                            </div>
                          </div>
                          <div className="adm-settings-row">
                            <div className="adm-muted">Host</div>
                            <div style={{ fontWeight: 500 }}>
                              {settingsData && settingsData.db && settingsData.db.host ? String(settingsData.db.host) : '-'}
                            </div>
                          </div>
                          <div className="adm-settings-row">
                            <div className="adm-muted">Uptime</div>
                            <div style={{ fontWeight: 500 }}>
                              {settingsData && typeof settingsData.uptime !== 'undefined' ? `${Math.floor(Number(settingsData.uptime || 0))}s` : '-'}
                            </div>
                          </div>
                        </div>

                        <div className="adm-settings-actions">
                          <button type="button" className="adm-btn" onClick={() => setSettingsOpen(false)}>
                            Close
                          </button>
                          <button
                            type="button"
                            className="adm-btn adm-btn--primary"
                            onClick={() => {
                              setSettingsOpen(false);
                              setActiveTab('settings');
                            }}
                          >
                            Open Settings
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="adm-profile-menu-wrap" ref={profileMenuWrapRef}>
                    <button
                      type="button"
                      className="adm-profile-avatar-btn"
                      title="Profile"
                      aria-haspopup="menu"
                      aria-expanded={profileMenuOpen ? 'true' : 'false'}
                      onClick={() => {
                        if (profileMenuOpen) {
                          setProfileMenuOpen(false);
                        } else {
                          openProfileMenu();
                        }
                      }}
                    >
                      {profileAvatarUrl ? (
                        <img className="adm-profile-avatar-img" src={profileAvatarUrl} alt="Profile" />
                      ) : (
                        <span className="adm-profile-avatar-fallback" aria-hidden="true">{profileInitials}</span>
                      )}
                      <span className="adm-profile-avatar-dots" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="14" height="14">
                          <path d="M12 5.2a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Zm0 8.4a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Zm0 8.4a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Z" fill="currentColor" />
                        </svg>
                      </span>
                    </button>

                    {profileMenuOpen ? (
                      <div className="adm-profile-dropdown" role="menu" aria-label="Profile actions">
                        <button
                          type="button"
                          className="adm-profile-menu-item"
                          role="menuitem"
                          onClick={() => {
                            setProfileMenuOpen(false);
                            setProfileOpen(true);
                          }}
                        >
                          <span className="adm-profile-menu-ico" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="18" height="18">
                              <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.41 0-8 2-8 4.5V21h16v-2.5C20 16 16.41 14 12 14Z" fill="currentColor" />
                            </svg>
                          </span>
                          <span className="adm-profile-menu-label">View Profile</span>
                        </button>

                        <button
                          type="button"
                          className="adm-profile-menu-item"
                          role="menuitem"
                          onClick={openChangeProfileImage}
                        >
                          <span className="adm-profile-menu-ico" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="18" height="18">
                              <path d="M4 7a3 3 0 0 1 3-3h2l1-1h4l1 1h2a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7Zm3-1a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-2.2l-1-1h-2.6l-1 1H7Zm5 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" fill="currentColor" />
                            </svg>
                          </span>
                          <span className="adm-profile-menu-label">Change Profile Image</span>
                        </button>

                        <button
                          type="button"
                          className="adm-profile-menu-item adm-profile-menu-item--danger"
                          role="menuitem"
                          onClick={() => {
                            setProfileMenuOpen(false);
                            onLogout();
                          }}
                        >
                          <span className="adm-profile-menu-ico" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="18" height="18">
                              <path d="M11 2h2v10h-2V2Zm7.07 3.93-1.41 1.41A7 7 0 1 1 7.34 7.34L5.93 5.93A9 9 0 1 0 18.07 5.93Z" fill="currentColor" />
                            </svg>
                          </span>
                          <span className="adm-profile-menu-label">Logout</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {activeTab === 'dashboard' ? (
                <div className="adm-headerbar adm-headerbar--sub">
                  <div className="adm-headerbar-left">
                    <div className="adm-headerbar-title">Dashboard</div>
                  </div>
                  <div className="adm-headerbar-actions">
                    <button type="button" className="adm-btn adm-btn--primary" onClick={loadDashboard} disabled={busy}>
                      {busy ? 'Loading…' : 'Refresh All'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="adm-page-head">
                  <div>
                    <div className="adm-page-title">{pageTitle}</div>
                  </div>
                </div>
              )}

              {success ? <div className="adm-success" style={{ marginBottom: 12 }}>{success}</div> : null}
              {error ? <div className="adm-error" style={{ marginBottom: 12 }}>{error}</div> : null}
              {renderList()}
            </main>
          </div>
        </div>
      )}

      <Modal open={profileOpen} title="Admin Profile" onClose={() => setProfileOpen(false)}>
        <div className="adm-profile-modal">
          <div className="adm-profile-hero">
            <div className="adm-profile-hero-avatar" aria-hidden="true">
              {profileAvatarUrl ? (
                <img className="adm-profile-hero-avatar-img" src={profileAvatarUrl} alt="Profile" />
              ) : (
                profileInitials
              )}
            </div>

            <div className="adm-profile-hero-meta">
              <div className="adm-profile-hero-name">{profileName}</div>
              <div className="adm-profile-hero-sub">Administrator</div>
              <div className="adm-profile-chip">{profileRole}</div>
            </div>

            <button type="button" className="adm-btn adm-btn--ghost" disabled>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58ZM20.71 7.04a1 1 0 0 0 0-1.41L18.37 3.29a1 1 0 0 0-1.41 0l-1.26 1.26 3.75 3.75 1.26-1.26Z"
                  fill="currentColor"
                />
              </svg>
              Edit Profile
            </button>
          </div>

          <div className="adm-profile-rows">
            <div className="adm-profile-row">
              <div className="adm-profile-row-label">Email</div>
              <div className="adm-profile-row-value">
                {String(tokenInfo && (tokenInfo.email || tokenInfo.sub || tokenInfo.username) ? tokenInfo.email || tokenInfo.sub || tokenInfo.username : '-')}
              </div>
            </div>

            <div className="adm-profile-row">
              <div className="adm-profile-row-label">Role</div>
              <div className="adm-profile-row-value">
                <span className="adm-profile-row-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path
                      d="M12 2 4 5v6c0 5 3.4 9.8 8 11 4.6-1.2 8-6 8-11V5l-8-3Zm0 18c-3.4-1.1-6-4.8-6-9V6.3l6-2.2 6 2.2V11c0 4.2-2.6 7.9-6 9Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                {String(tokenInfo && (tokenInfo.role || tokenInfo.type) ? tokenInfo.role || tokenInfo.type : '-')}
              </div>
            </div>
          </div>

        </div>
      </Modal>

      <Modal open={profileImageOpen} title="Change Profile Image" onClose={() => setProfileImageOpen(false)}>
        <div className="adm-avatar-modal">
          <div className="adm-avatar-preview">
            {String(profileImagePreview || '').trim() ? (
              <img className="adm-avatar-preview-img" src={profileImagePreview} alt="Preview" />
            ) : (
              <div className="adm-avatar-preview-fallback" aria-hidden="true">{profileInitials}</div>
            )}
          </div>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
              onSelectProfileAvatarFile(file);
            }}
          />

          <div className="adm-actions" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="adm-btn" onClick={() => setProfileImageOpen(false)}>
              Cancel
            </button>
            <button type="button" className="adm-btn adm-btn--primary" onClick={saveProfileAvatar} disabled={!String(profileImagePreview || '').trim()}>
              Save
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={profileEditOpen} title="Edit Profile" onClose={() => setProfileEditOpen(false)}>
        {profileEditError ? <div className="adm-error">{profileEditError}</div> : null}

        <div className="adm-section" style={{ display: 'grid', gap: 12 }}>
          <div className="adm-field">
            <label className="adm-label">Name</label>
            <input
              className="adm-input"
              value={profileEditForm.name || ''}
              onChange={(e) => setProfileEditForm((s) => ({ ...s, name: e.target.value }))}
              disabled={profileEditBusy}
            />
          </div>

          <div className="adm-field">
            <label className="adm-label">Email</label>
            <input
              className="adm-input"
              value={profileEditForm.email || ''}
              onChange={(e) => setProfileEditForm((s) => ({ ...s, email: e.target.value }))}
              disabled={profileEditBusy}
            />
          </div>

          <div className="adm-field">
            <label className="adm-label">New Password (optional)</label>
            <input
              className="adm-input"
              type="password"
              value={profileEditForm.password || ''}
              onChange={(e) => setProfileEditForm((s) => ({ ...s, password: e.target.value }))}
              placeholder="Leave blank to keep current password"
              disabled={profileEditBusy}
            />
          </div>

          <div className="adm-actions" style={{ justifyContent: 'space-between' }}>
            <button type="button" className="adm-btn adm-btn--danger" onClick={deleteMyAccount} disabled={profileEditBusy}>
              Delete Account
            </button>

            <div className="adm-actions">
              <button type="button" className="adm-btn" onClick={() => setProfileEditOpen(false)} disabled={profileEditBusy}>
                Cancel
              </button>
              <button type="button" className="adm-btn adm-btn--primary" onClick={saveProfile} disabled={profileEditBusy}>
                {profileEditBusy ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={settingsCrudOpen}
        title={settingsCrudMode === 'edit' ? 'Edit Setting' : 'Add Setting'}
        onClose={() => setSettingsCrudOpen(false)}
      >
        {settingsCrudError ? <div className="adm-error">{settingsCrudError}</div> : null}

        <div className="adm-section" style={{ display: 'grid', gap: 12 }}>
          <div className="adm-field">
            <label className="adm-label">Key</label>
            <input
              className="adm-input"
              value={settingsCrudForm.key || ''}
              onChange={(e) => setSettingsCrudForm((s) => ({ ...s, key: e.target.value }))}
              placeholder="siteTitle"
              disabled={settingsCrudBusy}
            />
          </div>

          <div className="adm-field">
            <label className="adm-label">Description</label>
            <input
              className="adm-input"
              value={settingsCrudForm.description || ''}
              onChange={(e) => setSettingsCrudForm((s) => ({ ...s, description: e.target.value }))}
              placeholder="Optional description"
              disabled={settingsCrudBusy}
            />
          </div>

          <div className="adm-field">
            <label className="adm-label">Value (JSON)</label>
            <textarea
              className="adm-textarea adm-code"
              rows={10}
              value={settingsCrudForm.valueText || ''}
              onChange={(e) => setSettingsCrudForm((s) => ({ ...s, valueText: e.target.value }))}
              placeholder={`{\n  "enabled": true\n}`}
              disabled={settingsCrudBusy}
            />
          </div>

          <div className="adm-actions" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="adm-btn" onClick={() => setSettingsCrudOpen(false)} disabled={settingsCrudBusy}>
              Cancel
            </button>
            <button type="button" className="adm-btn adm-btn--primary" onClick={saveSettingsItem} disabled={settingsCrudBusy}>
              {settingsCrudBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={createOpen} title={createTitle} onClose={() => setCreateOpen(false)}>
        {activeTab !== 'adminUsers' ? (
          <div className="adm-actions">
            <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={createAdvanced} onChange={(e) => setCreateAdvanced(e.target.checked)} />
              Advanced (JSON)
            </label>
          </div>
        ) : null}

        {!createAdvanced && activeTab === 'doctorsTimeline' ? (
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            <textarea
              placeholder="Doctor name (you can use a new line for 2-line names)"
              value={createForm.doctorName || ''}
              onChange={(e) => setCreateForm((s) => ({ ...s, doctorName: e.target.value }))}
              rows={2}
              style={{ padding: 10, fontFamily: 'inherit' }}
            />
            <input
              placeholder="Qualification / subtitle"
              value={createForm.qualification || ''}
              onChange={(e) => setCreateForm((s) => ({ ...s, qualification: e.target.value }))}
              style={{ padding: 10 }}
            />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                type="number"
                placeholder="Order"
                value={String(createForm.position ?? 0)}
                onChange={(e) => setCreateForm((s) => ({ ...s, position: Number(e.target.value || 0) }))}
                style={{ padding: 10, width: 160 }}
              />
              <select
                value={String(createForm.status || 'active')}
                onChange={(e) => setCreateForm((s) => ({ ...s, status: e.target.value }))}
                style={{ padding: 10, width: 160 }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        ) : null}

        {!createAdvanced && activeTab === 'appointments' ? (
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            <input placeholder="Name" value={createForm.name || ''} onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))} style={{ padding: 10 }} />
            <input placeholder="Phone" value={createForm.phone || ''} onChange={(e) => setCreateForm((s) => ({ ...s, phone: e.target.value }))} style={{ padding: 10 }} />
            <input placeholder="Email (optional)" value={createForm.email || ''} onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))} style={{ padding: 10 }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input placeholder="Preferred date" value={createForm.preferred_date || ''} onChange={(e) => setCreateForm((s) => ({ ...s, preferred_date: e.target.value }))} style={{ padding: 10, flex: 1 }} />
              <input placeholder="Preferred time" value={createForm.preferred_time || ''} onChange={(e) => setCreateForm((s) => ({ ...s, preferred_time: e.target.value }))} style={{ padding: 10, flex: 1 }} />
            </div>
            <textarea placeholder="Notes" value={createForm.notes || ''} onChange={(e) => setCreateForm((s) => ({ ...s, notes: e.target.value }))} rows={5} style={{ padding: 10, fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <select value={createForm.status || 'new'} onChange={(e) => setCreateForm((s) => ({ ...s, status: e.target.value }))} style={{ padding: 10 }}>
                <option value="new">new</option>
                <option value="confirmed">confirmed</option>
                <option value="declined">declined</option>
                <option value="completed">completed</option>
              </select>
            </div>
          </div>
        ) : null}

        {!createAdvanced && activeTab === 'adminUsers' ? (
          <div className="adm-sec-form" style={{ marginTop: 12 }}>
            <div className="adm-sec-form-row">
              <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                <label className="adm-label">Name</label>
                <input className="adm-input" value={createForm.name || ''} onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))} />
              </div>
              <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                <label className="adm-label">Email</label>
                <input className="adm-input" value={createForm.email || ''} onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))} />
              </div>
            </div>

            <div className="adm-sec-form-row">
              <div className="adm-field" style={{ flex: 1, minWidth: 240 }}>
                <label className="adm-label">Password</label>
                <input
                  className="adm-input"
                  type="password"
                  placeholder="Min 8 characters"
                  value={createForm.password || ''}
                  onChange={(e) => setCreateForm((s) => ({ ...s, password: e.target.value }))}
                />
              </div>
              <div className="adm-field" style={{ width: 200 }}>
                <label className="adm-label">Role</label>
                <select
                  className="adm-select"
                  value={createForm.role || 'admin'}
                  onChange={(e) => {
                    const role = e.target.value;
                    setCreateForm((s) => ({
                      ...s,
                      role,
                      permissions: {
                        ...defaultPermissionsForRole(role),
                        ...(s && s.permissions && typeof s.permissions === 'object' ? s.permissions : {}),
                      },
                    }));
                  }}
                >
                  <option value="super_admin">super_admin</option>
                  <option value="admin">admin</option>
                  <option value="editor">editor</option>
                  <option value="staff">staff</option>
                </select>
              </div>
              <div className="adm-field" style={{ width: 160, display: 'flex', alignItems: 'flex-end' }}>
                <label className="adm-muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={Boolean(createForm.is_active)} onChange={(e) => setCreateForm((s) => ({ ...s, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
            </div>

            <div className="adm-list-title" style={{ marginTop: 6 }}>Access Control</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { key: 'appointments.manage', label: 'Appointments (manage)' },
                { key: 'inquiries.manage', label: 'Inquiries (manage)' },
                { key: 'feedback.manage', label: 'Feedback (manage)' },
                { key: 'notifications.read', label: 'Notifications (read)' },
                { key: 'settings.manage', label: 'Settings (manage)' },
                { key: 'content.manage', label: 'Content (manage)' },
                { key: 'adminUsers.manage', label: 'Admin Users (manage)' },
              ].map((p) => {
                const roleLower = String(createForm.role || '').trim().toLowerCase();
                const disabled = roleLower === 'admin';
                const perms = createForm && createForm.permissions && typeof createForm.permissions === 'object' ? createForm.permissions : {};
                const checked = Boolean(perms && Object.prototype.hasOwnProperty.call(perms, p.key) ? perms[p.key] : false);

                return (
                  <label key={p.key} className="adm-muted" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) =>
                        setCreateForm((s) => ({
                          ...s,
                          permissions: {
                            ...(s && s.permissions && typeof s.permissions === 'object' ? s.permissions : {}),
                            [p.key]: e.target.checked,
                          },
                        }))
                      }
                    />
                    {p.label}
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}

        {!createAdvanced && activeTab === 'inquiries' ? (
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            <input placeholder="Name" value={createForm.name || ''} onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))} style={{ padding: 10 }} />
            <input placeholder="Email" value={createForm.email || ''} onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))} style={{ padding: 10 }} />
            <input placeholder="Phone" value={createForm.phone || ''} onChange={(e) => setCreateForm((s) => ({ ...s, phone: e.target.value }))} style={{ padding: 10 }} />
            <textarea placeholder="Message" value={createForm.message || ''} onChange={(e) => setCreateForm((s) => ({ ...s, message: e.target.value }))} rows={5} style={{ padding: 10, fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input placeholder="Status" value={createForm.status || ''} onChange={(e) => setCreateForm((s) => ({ ...s, status: e.target.value }))} style={{ padding: 10, flex: 1 }} />
              <input placeholder="Assigned User ID" value={createForm.assigned_to_user_id || ''} onChange={(e) => setCreateForm((s) => ({ ...s, assigned_to_user_id: e.target.value }))} style={{ padding: 10, flex: 1 }} />
            </div>
          </div>
        ) : null}

        {!createAdvanced && activeTab === 'programs' ? (
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            <input
              placeholder="Program name"
              value={createForm.title || ''}
              onChange={(e) => {
                const title = e.target.value;
                setCreateForm((s) => ({ ...s, title }));
              }}
              style={{ padding: 10 }}
            />
            <textarea placeholder="Short description" value={createForm.shortDescription || ''} onChange={(e) => setCreateForm((s) => ({ ...s, shortDescription: e.target.value }))} rows={3} style={{ padding: 10, fontFamily: 'inherit' }} />
            <textarea placeholder="Bullet points (max 3, one per line)" value={createForm.bulletsText || ''} onChange={(e) => setCreateForm((s) => ({ ...s, bulletsText: e.target.value }))} rows={4} style={{ padding: 10, fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input placeholder="Icon style (optional)" value={createForm.iconType || ''} onChange={(e) => setCreateForm((s) => ({ ...s, iconType: e.target.value }))} style={{ padding: 10, flex: 1 }} />
              <input type="number" placeholder="Display order" value={String(createForm.displayOrder ?? 0)} onChange={(e) => setCreateForm((s) => ({ ...s, displayOrder: Number(e.target.value || 0) }))} style={{ padding: 10, width: 160 }} />
              <select value={String(createForm.status || 'active')} onChange={(e) => setCreateForm((s) => ({ ...s, status: e.target.value }))} style={{ padding: 10, width: 160 }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        ) : null}

        {!createAdvanced && activeTab === 'testimonials' ? (
          <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            <input placeholder="Patient display name" value={createForm.patientDisplayName || ''} onChange={(e) => setCreateForm((s) => ({ ...s, patientDisplayName: e.target.value }))} style={{ padding: 10 }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input type="number" placeholder="Age (optional)" value={createForm.age || ''} onChange={(e) => setCreateForm((s) => ({ ...s, age: e.target.value }))} style={{ padding: 10, width: 160 }} />
              <input placeholder="Category" value={createForm.category || ''} onChange={(e) => setCreateForm((s) => ({ ...s, category: e.target.value }))} style={{ padding: 10, flex: 1 }} />
            </div>
            <textarea placeholder="Quote" value={createForm.quote || ''} onChange={(e) => setCreateForm((s) => ({ ...s, quote: e.target.value }))} rows={5} style={{ padding: 10, fontFamily: 'inherit' }} />
            <input placeholder="Outcome label (optional)" value={createForm.outcomeLabel || ''} onChange={(e) => setCreateForm((s) => ({ ...s, outcomeLabel: e.target.value }))} style={{ padding: 10 }} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input placeholder="Treatment duration (optional)" value={createForm.treatmentDuration || ''} onChange={(e) => setCreateForm((s) => ({ ...s, treatmentDuration: e.target.value }))} style={{ padding: 10, flex: 1 }} />
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={Boolean(createForm.isPublished)} onChange={(e) => setCreateForm((s) => ({ ...s, isPublished: e.target.checked }))} />
                Published
              </label>
            </div>
          </div>
        ) : null}

        {createAdvanced && activeTab !== 'adminUsers' ? (
          <div style={{ marginTop: 12 }}>
            <textarea value={payloadText} onChange={(e) => setPayloadText(e.target.value)} rows={12} style={{ width: '100%', fontFamily: 'monospace', padding: 10 }} />
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button type="button" className="adm-btn" onClick={() => setCreateOpen(false)} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="adm-btn adm-btn--success" onClick={submitCreateForm} disabled={busy}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default FullAdminPanel;
